import { LocationType } from '../types';

// Predefined bounding boxes for each area type. More efficient than fetching and processing geoJSON files.
// Generated by querying nominatim API for each country, and fixed issues using https://boundingbox.klokantech.com/.

// Challenges:
// - will need to be updated together with selection lists
// - mapbox doesn't support longitude ranges outside of [-180, 180] without rendering multiple world copies
// - world-wide bboxes crossing the 180th Meridian are not handled correctly (ex: Russia)
// - mapbox latitude range should be within [-85.051129, 85.051129]
// - need to refocus the bbox of countries with lots of remote islands to the main metropolitan / continental area (ex: France)
// - same thing for regional bboxes, in particular Western Europe shouldn't include Reunion island in the India Ocean
// - archipelagos bboxes should point to the main / labelled island to be useful (empty ocean view otherwise)
// - some countries have multiple accepted names (Turkiye vs Turkey)
// - the countries selection list contains non-country entries like islands, antarctica, etc.
export const BoundingBoxes: Record<
  LocationType,
  Record<string, [number, number, number, number]>
> = {
  [LocationType.REGION]: {
    'north america': [-178.21656, 17.877, 0, 83.62025],
    'southern asia': [44.03495, -7.43625, 97.38054, 39.77915],
    'western europe': [
      -5.2015191362, 41.1700199366, 17.4382132246, 55.0820205795,
    ],
    'southern america': [-109.44527, -55.90223, -34.79292, 12.59027],
    'south america': [-109.44527, -55.90223, -34.79292, 12.59027],
    'northern europe': [-24.5384, 49.95527, 33.6375, 80.76125],
    'eastern asia': [73.62005, 18.16888, 145.81241, 53.55375],
    'south eastern asia': [92.21485, -12.19347, 141.00702, 28.54653],
    'south-eastern asia': [92.21485, -12.19347, 141.00702, 28.54653],
    'central america and the caribbean': [
      -118.40416, 7.22027, -59.42917, 32.71845,
    ],
    'southern europe': [-9.49084, 34.64027, 34.58604, 47.09458],
    'eastern europe': [9.5307487, 41.20776, 180, 81.8193],
    'sub-saharan africa': [-25.36055, -53.19944, 73.77388, 27.29045],
    'pacific islands': [
      112.328440547, -47.3492729189, 179.9108380079, 0.1493722185,
    ],
    'central and western asia': [25.66583, 29.48671, 87.34821, 55.44263],
    'arab states': [34.21666, 12.15305, 59.8325, 37.38367],
    'northern africa': [-31.28903, 8.68497, 38.6075, 39.52195],
    antarctic: [-179.99999, -85, 0, -54.00744],
  },
  [LocationType.COUNTRY]: {
    afghanistan: [60.5176034, 29.3772926, 74.889862, 38.4910682],
    albania: [19.0009866, 39.640394, 21.0574335, 42.6610848],
    algeria: [-8.668908, 18.968147, 11.997337, 37.2962055],
    'united states': [
      -168.3682153746, 24.0188791828, -64.3555260077, 71.588953465,
    ],
    andorra: [1.4077997, 42.4288238, 1.7868662, 42.6559357],
    angola: [11.4609793, -18.0393809, 24.0876359, -4.3458006],
    'united kingdom': [-14.015517, 49.674, 2.0919117, 61.061],
    antarctica: [-180.0, -85, 180.0, -59.9999999],
    'antigua and barbuda': [-62.5548313, 16.755628, -61.4476424, 17.9473002],
    argentina: [-73.5605371, -55.1925709, -53.6374515, -21.7808568],
    armenia: [43.4471395, 38.8404775, 46.6333087, 41.3016463],
    netherlands: [3.2981, 50.7194, 7.2275, 53.7444],
    australia: [112.6538763195, -44.262174066, 154.2978964001, -10.5117898554],
    austria: [9.5307487, 46.3722987, 17.1607728, 49.0205249],
    azerbaijan: [44.7633701, 38.3929551, 51.1765554, 41.9646768],
    portugal: [-9.5005266, 38.4090745, -8.4909721, 39.0647184],
    bahamas: [-80.7001941, 20.7059846, -72.4477521, 27.4734551],
    bahrain: [50.2697989, 25.535, 50.9233651, 26.6872444],
    bangladesh: [88.007915, 20.3679092, 92.680196, 26.6358859],
    barbados: [-59.8562115, 12.845, -59.2147175, 13.535],
    belarus: [23.1783313, 51.2626864, 32.7627809, 56.1722484],
    belgium: [2.3889137, 49.4969821, 6.408097, 51.550781],
    belize: [-89.2275897, 15.8857286, -87.2765017, 18.4959143],
    benin: [0.776667, 6.0398696, 3.8451454, 12.4092028],
    bhutan: [88.7462357, 26.7014664, 92.1252321, 28.2474194],
    bolivia: [-69.6450073, -22.8982742, -57.453, -9.6689438],
    'bosnia and herzegovina': [15.7287435, 42.5553114, 19.6237311, 45.2764141],
    botswana: [19.9986486, -26.9070073, 29.3738868, -17.778137],
    norway: [4.3605790515, 57.7368763135, 31.9960008045, 71.2509230941],
    brazil: [-73.9830625, -33.8694284, -28.6289646, 5.2695808],
    brunei: [113.017925, 4.002508, 115.3635623, 6.546584],
    bulgaria: [22.3571459, 41.2353678, 28.8875409, 44.2162232],
    'burkina faso': [-5.513207, 9.4104718, 2.4089717, 15.0840044],
    burundi: [29.000716, -4.4693155, 30.8498462, -2.3096796],
    'cape verde': [-25.5731073, 14.6066229, -22.4547752, 17.407117],
    cambodia: [102.3338282, 9.4110961, 107.6312258, 14.6904224],
    cameroon: [8.3822176, 1.6517945, 16.1911011, 13.083333],
    canada: [-141.00275, 41.6765597, -52.3237664, 83.3362128],
    spain: [-9.65, 35.95, 4.59, 43.99],
    'central african republic': [14.4148457, 2.222929, 27.4665499, 11.001389],
    chad: [13.47348, 7.44107, 24.0, 23.4507957],
    chile: [-109.6795789, -56.725, -66.0753474, -17.4983832],
    china: [73.4997347, 8.6650385, 134.7754563, 53.5608154],
    colombia: [-82.1243611, -4.2280906, -66.8511118, 16.0495518],
    comoros: [42.8262181, -12.9854371, 44.9730591, -11.1624399],
    'republic of the congo': [11.043616, -5.149089, 18.643611, 3.713056],
    'democratic republic of the congo': [
      12.039074, -13.459035, 31.3056758, 5.3920026,
    ],
    'congo -republic of the congo': [11.043616, -5.149089, 18.643611, 3.713056],
    'congo -democratic republic of the congo': [
      12.039074, -13.459035, 31.3056758, 5.3920026,
    ],
    'new zealand': [165.635033, -47.442137, 178.774681, -33.96059],
    'costa rica': [-87.1019965, 5.4991601, -82.4299043, 11.2195684],
    'ivory coast': [-8.6014675, 4.1621205, -2.492943, 10.740197],
    croatia: [13.2104814, 42.1765993, 19.4472713, 46.5550254],
    cuba: [-85.1679702, 19.6275294, -73.9190004, 23.4816972],
    cyprus: [32.0227581, 34.4383706, 34.3338842, 35.3991245],
    czechia: [12.0905752, 48.5518081, 18.8592531, 51.0557008],
    'czech republic': [12.0905752, 48.5518081, 18.8592531, 51.0557008],
    denmark: [7.7153, 54.4517, 15.5531, 57.9524],
    djibouti: [41.7713139, 10.9149547, 43.6579046, 12.7923081],
    dominica: [-61.6869184, 15.0074207, -61.0329895, 15.7872222],
    'dominican republic': [-72.0696319, 17.2701708, -68.1101463, 21.2928771],
    ecuador: [-92.2072392, -5.0159314, -75.192504, 1.8835964],
    egypt: [24.6499112, 21.9936736, 37.1153517, 31.8330854],
    'el salvador': [-90.2209042, 12.9518017, -87.5971467, 14.4510488],
    'equatorial guinea': [5.4172943, -1.6732196, 11.4113617, 3.989],
    eritrea: [36.4333653, 12.3548219, 43.3001714, 18.0709917],
    estonia: [21.3826069, 57.5093328, 28.2100175, 59.9383333],
    eswatini: [30.7908, -27.31752, 32.134992, -25.71876],
    ethiopia: [32.9975838, 3.397448, 47.9823797, 14.8943383],
    fiji: [176.6902281716, -19.5011972497, 180, -15.8496187537],
    finland: [19.0832, 59.4541578, 31.5867071, 70.092293],
    france: [-5.00230768, 41.2633125082, 9.8112108442, 51.3055721126],
    gabon: [8.5002246, -4.126909, 14.52902, 2.3192896],
    gambia: [-17.0223778, 13.0558333, -13.797778, 13.8253137],
    georgia: [39.8845, 41.0551, 46.7365, 43.5864],
    germany: [5.8663153, 47.2701114, 15.0419309, 55.099161],
    ghana: [-3.260786, 4.5392525, 1.2732942, 11.1748562],
    greece: [19.1127535, 34.7188863, 29.68381, 41.7488889],
    grenada: [-62.0072687, 11.7759051, -61.2095018, 12.6097247],
    guatemala: [-92.3648725, 13.563429, -88.2134425, 17.8165947],
    guinea: [-15.5680508, 7.1906045, -7.6379222, 12.67563],
    'guinea-bissau': [-16.897501, 10.6514215, -13.6348777, 12.6862384],
    guyana: [-61.3827449, 1.1710017, -56.4689543, 8.6038842],
    haiti: [-75.2384618, 17.820859, -71.6217461, 20.2911852],
    honduras: [-89.3568207, 12.9808485, -82.18164, 17.619522],
    hungary: [16.1138866, 45.737128, 22.8965048, 48.585257],
    iceland: [-25.0135069, 63.0859177, -12.8046162, 67.353],
    india: [67.9544415, 6.5531169, 97.395561, 35.6745457],
    indonesia: [94.7717124, -11.2085669, 141.0194444, 6.2744496],
    iran: [44.0318908, 24.8353084, 63.3332704, 39.7824624],
    iraq: [38.7926388, 29.0585661, 49.1067706, 37.3806687],
    ireland: [-11.0133788, 51.222, -5.6582363, 55.636],
    israel: [34.2674994, 29.4533796, 35.8950234, 33.3356317],
    italy: [6.6272658, 35.2889616, 18.7844746, 47.0921485],
    jamaica: [-78.5782366, 16.5899443, -75.7541143, 18.7256394],
    japan: [122.7141754, 20.2145811, 154.205541, 45.7112046],
    jordan: [34.8844372, 29.183401, 39.2998604, 33.373435],
    kazakhstan: [46.4932179, 40.5686902, 87.3156336, 55.4421701],
    kenya: [33.9096888, -4.8995204, 41.9067502, 4.62],
    kiribati: [-157.7911, 1.483, -156.9095, 2.2681],
    kuwait: [46.5526837, 28.5243622, 49.0046809, 30.1038082],
    kyrgyzstan: [69.2649523, 39.1727382, 80.2295793, 43.2669041],
    laos: [100.0843247, 13.9096752, 107.6349989, 22.5086717],
    latvia: [20.6009852, 55.6746505, 28.2414937, 58.0855688],
    lebanon: [34.8825667, 33.0550325, 36.625, 34.6921448],
    lesotho: [27.0113763, -30.6780352, 29.4557099, -28.5705652],
    liberia: [-11.6080764, 4.1555907, -7.367323, 8.5519251],
    libya: [9.391081, 19.4999907, 25.3770629, 33.3545898],
    liechtenstein: [9.4715658, 47.0484291, 9.6357143, 47.270581],
    lithuania: [20.6557167, 53.8967893, 26.8355198, 56.4504213],
    luxembourg: [5.7356988, 49.4478587, 6.5312481, 50.1827989],
    madagascar: [42.9680076, -25.784021, 50.6727307, -11.732889],
    malawi: [32.6718589, -17.1296031, 35.9185428, -9.368301],
    malaysia: [98.7365109, 0.8538205, 119.4699634, 8.3801468],
    maldives: [72.3554187, -0.9074935, 73.9700962, 7.3106246],
    mali: [-12.2402835, 10.147811, 4.2673828, 25.001084],
    malta: [13.9360446, 35.5853691, 14.822786, 36.2825455],
    'marshall islands': [168.0002075, 8.9995049, 168.0003075, 8.9996049],
    mauritania: [-17.2380959, 14.7209909, -4.8333344, 27.314942],
    mauritius: [57.1114, -20.7286, 58.0477, -19.7537],
    mexico: [-118.599188, 14.3811832, -86.493266, 32.7187133],
    micronesia: [137.1344444, 0.8232833, 163.2374639, 10.2933306],
    moldova: [26.6164054, 45.4671885, 30.1637143, 48.4920527],
    monaco: [7.4090279, 43.5165358, 7.5329917, 43.7519173],
    mongolia: [87.7345852, 41.5800276, 119.9322079, 52.1496],
    montenegro: [18.4195781, 41.6849866, 20.3529276, 43.5585061],
    morocco: [-17.3190239, 20.667252, -0.998429, 36.0021392],
    mozambique: [30.2121663, -26.9209427, 41.0545908, -10.3252149],
    myanmar: [92.1729181, 9.526084, 101.1700796, 28.547835],
    namibia: [11.5280384, -28.96945, 25.2617292, -16.9635105],
    nauru: [166.7106737, -0.7526654, 167.1600334, -0.3033259],
    nepal: [80.0570812, 26.3477581, 88.2015257, 30.446945],
    nicaragua: [-87.8958171, 10.7073945, -82.4767247, 15.0902778],
    niger: [0.1689653, 11.6936071, 15.996667, 23.517178],
    nigeria: [2.676932, 4.0690959, 14.678014, 13.885645],
    'north korea': [124.125393, 37.5833302, 130.8861177, 43.0089642],
    'north macedonia': [20.4529023, 40.852478, 23.034051, 42.3739044],
    macedonia: [20.4529023, 40.852478, 23.034051, 42.3739044],
    oman: [52.0000004, 16.4649608, 60.054577, 26.702678],
    pakistan: [60.872855, 23.4341977, 77.1203914, 37.084107],
    palau: [130.9193911, 2.595824, 134.9295484, 8.405805],
    palestine: [34.181213, 31.23394, 35.576477, 32.539867],
    panama: [-83.0518594, 7.0027171, -77.1585751, 9.8469028],
    'papua new guinea': [140.8416553, -11.8555739, 159.6934961, -0.5573576],
    paraguay: [-62.6406685, -27.6063935, -54.258, -19.2876472],
    peru: [-84.6356535, -20.1984472, -68.6519906, -0.0392818],
    philippines: [114.1003696, 4.3833333, 126.803083, 21.321928],
    poland: [14.0696389, 49.0020468, 24.145783, 55.03605],
    qatar: [50.5675, 24.4707534, 52.638011, 26.434565],
    romania: [20.2619955, 43.6188114, 30.0454257, 48.2654738],
    russia: [19.4041718915, 41.1850968828, 180, 82.0586232312],
    rwanda: [28.861696, -2.8397643, 30.8990738, -1.0474083],
    'saint kitts and nevis': [-63.0491098, 16.8926227, -62.3741863, 17.6170552],
    'saint lucia': [-61.2855618, 13.5060753, -60.6640451, 14.2725],
    'saint vincent and the grenadines': [
      -61.6645712, 12.3983584, -60.9087552, 13.5843008,
    ],
    samoa: [-173.0091864, -14.2770916, -171.1929229, -13.2381892],
    'san marino': [12.402433, 43.893632, 12.5169591, 43.992042],
    'sao tome and principe': [6.260642, -0.2135137, 7.6704783, 1.9257601],
    'saudi arabia': [34.4571718, 16.29, 55.6666851, 32.1542832],
    senegal: [-17.7498686, 12.2402664, -11.3459503, 16.6919712],
    serbia: [18.8149945, 41.8576408, 23.006309, 46.1902839],
    seychelles: [45.9988759, -10.4649258, 56.4979396, -3.512],
    'sierra leone': [-13.5003389, 6.755, -10.271683, 9.999973],
    singapore: [103.5655616, 1.1265744, 104.5716696, 1.5143183],
    slovakia: [16.8331891, 47.7311798, 22.5657103, 49.6138162],
    slovenia: [13.3754696, 45.4214681, 16.5967003, 46.8766816],
    'solomon islands': [155.3190556, -13.2424298, 170.3964667, -4.8127915],
    somalia: [40.98918, -1.8031969, 51.6177696, 12.1889121],
    'south africa': [16.33, -35.1, 33.12, -22.13],
    'south korea': [124.3727348, 32.9104556, 132.1467806, 38.61772],
    'south sudan': [23.447778, 3.48898, 35.948997, 12.236389],
    'sri lanka': [79.421989, 5.719, 82.0810141, 10.035],
    sudan: [21.8143904, 8.685278, 39.0576252, 22.224918],
    suriname: [-58.0711851, 1.8312802, -53.8601382, 6.2160842],
    sweden: [10.5935025, 55.1370957, 24.1776819, 69.0599735],
    switzerland: [5.9558318, 45.8179579, 10.4922941, 47.8084544],
    swaziland: [30.7908, -27.31752, 32.134992, -25.71876],
    syria: [35.4714427, 32.3117576, 42.3745894, 37.3186819],
    tajikistan: [67.3332775, 36.6711153, 75.1539563, 41.0450935],
    tanzania: [29.3269773, -11.761254, 40.6584071, -0.9854812],
    thailand: [97.3438072, 5.612851, 105.636812, 20.4648135],
    'timor-leste': [124.0339863, -9.6096854, 127.5433663, -8.1161123],
    togo: [-0.1439746, 5.926547, 1.8087257, 11.1395102],
    tonga: [-179.3950218, -24.1635435, -173.5295458, -15.3655722],
    'trinidad and tobago': [-62.083056, 9.8732106, -60.2895848, 11.5628372],
    tunisia: [7.5219807, 30.229061, 11.8801133, 37.7612052],
    turkey: [25.5656305, 35.8058974, 44.8176638, 42.297],
    turkmenistan: [52.2643526, 35.129093, 66.6903725, 42.7996686],
    tuvalu: [177.487693, -9.034015, 179.32241, -7.195999],
    uganda: [29.573433, -1.4823179, 35.000308, 4.2340766],
    ukraine: [22.137059, 44.184598, 40.2278093, 52.3797464],
    'united arab emirates': [51.4160714, 22.6316214, 56.6024458, 26.1517219],
    uruguay: [-58.4947729, -35.7824829, -53.0755833, -30.0853962],
    uzbekistan: [55.9985531, 37.1816004, 73.2147728, 45.5906784],
    vanuatu: [166.3355255, -20.4627425, 170.449982, -12.8713777],
    vatican: [12.4328349, 41.8834912, 12.4728349, 41.9234912],
    venezuela: [-73.3529632, 0.6473964, -59.7707163, 15.9158431],
    vietnam: [102.1438643, 7.6920852, 114.8572578, 23.3926918],
    yemen: [41.60825, 11.9084802, 54.7389375, 19.0],
    zambia: [21.9990553, -18.0762145, 33.7088556, -8.2749338],
    zimbabwe: [25.2373, -22.4241096, 33.0683501, -15.6097033],
    bermuda: [-65.1232222, 32.0469651, -64.4109842, 32.5913693],
    reunion: [
      // messes up western europe
      55.0032808, -21.5904458, 56.0507905, -20.672764,
    ],
    jersey: [-2.5605483, 48.871203, -1.8347, 49.4596091],
    'isle of man': [-5.1707285, 53.845, -3.9654061, 54.5534288],
    guernsey: [-2.6751703, 49.4155331, -2.501814, 49.5090776],
    'french guiana': [-54.6026945, 2.1109401, -51.5045192, 5.9838892],
    greenland: [-73.0263201, 59.9737205, -11.3511087, 83.6608453],
    'faroe islands': [-7.6918682, 61.3379074, -6.2562199, 62.3942991],
    'aland islands': [19.0832, 59.4541578, 21.3456452, 60.87665],
    'saint barthelemy': [-63.06639, 17.670931, -62.5844019, 18.1375569],
    'bonaire saint eustatius and saba': [
      -68.6255319, 11.825, -62.7847057, 17.85,
    ],
    'saint pierre and miquelon': [
      -56.6974627, 46.5488309, -55.9066442, 47.3415794,
    ],
    'falkland islands': [-61.7726772, -53.1186766, -57.3662367, -50.7973007],
    'united states minor outlying islands': [
      -179.1211, -0.8789, -159.1699, 28.7677,
    ],
    'svalbard and jan mayen': [9.4208924, 74.1354451, 34.6889114, 81.0280176],
    svalbard: [9.4208924, 74.1354451, 34.6889114, 81.0280176],
    taiwan: [118.9844942652, 21.6251842316, 122.3282998055, 25.6342403641],
    'new caledonia': [
      163.3697837591, -22.8307904495, 168.2178872824, -19.4358531159,
    ],
    'american samoa': [
      -171.141907163, -14.6018129466, -170.3526079841, -14.0133347645,
    ],
    'wallis and futuna': [
      -176.4574635588, -13.5267839227, -175.9325659834, -13.0865387944,
    ],
    guam: [144.5634259656, 13.1823350687, 145.0091668591, 13.7061790725],
    'french polynesia': [
      -150.2500458111, -18.3037813674, -148.7347479214, -17.0791999523,
    ],
    'cocos islands': [
      96.7878113687, -12.243693763, 96.9624178112, -12.0394341754,
    ],
    tokelau: [-171.8982165121, -9.24228966, -171.7247097753, -9.0833842308],
    'christmas island': [
      105.3301448375, -10.771406508, 105.9158045053, -10.2114748577,
    ],
    niue: [-170.1595031843, -19.3548663262, -169.5647229999, -18.7534557177],
    'cook islands': [
      -160.0474836677, -21.4792214707, -159.5077585056, -20.9949898049,
    ],
    'norfolk island': [
      167.6856726408, -29.3370693537, 168.2261256874, -28.7936624131,
    ],
    'northern mariana islands': [
      145.5130593016, 14.9163782548, 145.8662054354, 15.2988159716,
    ],
    macao: [113.5281664878, 22.0766667147, 113.6301387101, 22.2170486091],
    'palestinian territory': [
      34.0546905622, 31.2201289276, 35.5739232525, 32.5521480655,
    ],
    'cayman islands': [
      -81.6313751042, 19.0620621106, -79.5110955834, 19.9573760064,
    ],
    'cabo verde': [
      -25.5731074512, 14.6066231468, -22.4547752738, 17.4071170841,
    ],
    kosovo: [20.0142842531, 41.8576410262, 21.7899364606, 43.2676633383],
    'british virgin islands': [
      -64.9559612945, 18.1055312576, -64.0597296506, 18.9503802671,
    ],
    'virgin islands (uk)': [
      -64.9559612945, 18.1055312576, -64.0597296506, 18.9503802671,
    ],
    curacao: [-69.3673495576, 11.7770000686, -68.4413350001, 12.5920002693],
    guadeloupe: [-62.0180391893, 15.6444112998, -60.7919843495, 16.7140001782],
    aruba: [-70.2695682272, 12.2578296837, -69.6612355113, 12.8230000202],
    martinique: [-61.4355046302, 14.2217382533, -60.6022814289, 15.0795034491],
    gibraltar: [-5.4006553441, 36.0587551464, -5.27666118, 36.1550377048],
    'timor leste': [
      124.0339862555, -9.6096853972, 127.5433576852, -8.1161164233,
    ],
    mayotte: [44.7436673194, -13.273255224, 45.5070343986, -12.3790267563],
    'guinea bissau': [
      -16.8975010514, 10.6514216524, -13.6348777637, 12.6862385603,
    ],
    anguilla: [-63.2835950889, 18.1279178735, -62.9148342647, 18.3093940627],
    'puerto rico': [
      -68.1108798087, 17.7306659963, -65.1100910828, 18.6663824908,
    ],
    'saint helena': [
      -5.9973425791, -16.2299999257, -5.423415564, -15.7039997854,
    ],
    'british indian ocean territory': [
      71.9327512313, -7.8139840713, 72.9945197631, -6.972545343,
    ],
    montserrat: [-62.4502433091, 16.4737637215, -61.9359977916, 16.8885157409],
    'saint martin': [
      -63.360564597, 17.8963535117, -62.7644064277, 18.1902780062,
    ],
    'u s virgin islands': [
      -65.1541175321, 17.6234681162, -64.512674287, 18.4649841585,
    ],
    'western sahara': [-17.2705, 21.187, -8.4814, 28.1107],
    'sint maarten': [
      -63.2688119262, 17.8062092264, -62.9494453222, 18.0639863909,
    ],
    'turks and caicos islands': [
      -72.6799048483, 20.9553421006, -70.8643591404, 22.1630989898,
    ],
    'french southern territories': [
      39.4138675556, -50.2187168358, 77.8494972736, -11.3139926937,
    ],
    'hong kong': [113.8172405586, 22.1367248073, 114.5024866238, 22.5683333398],
  },
  [LocationType.METRO_AREA]: {
    '1': [-0.7621, 51.0034, 0.95749, 52.0927],
    '2': [-75.648471, 39.498537, -71.856214, 41.632597],
    '3': [1.1834, 48.0524, 3.5835, 49.5473],
    '4': [68.162379, 6.752921, 97.39538, 35.511255],
    '5': [-47.107623, -23.932607, -45.958574, -23.06323],
    '6': [-73.982825, -33.750987, -28.842988, 5.27177],
    '7': [76.729994, 28.238659, 77.895031, 29.199428],
    '8': [103.605549, 1.214614, 104.085693, 1.431335],
    '9': [77.264855, 12.6726, 77.947384, 13.277042],
    '10': [-80.813676, 43.286317, -78.08215, 44.985642],
    '11': [-88.942146, 40.736541, -86.929364, 42.669709],
    '12': [-178.192954, -49.734612, 172.057935, 51.089542],
    '13': [4.4938, 52.1065, 5.3792, 52.9375],
    '14': [-78.386682, 37.990928, -76.319476, 39.720018],
    '15': [-118.944887, 33.874832, -117.748733, 34.821244],
    '16': [149.976419, -34.323387, 151.630547, -32.996069],
    '17': [-71.898716, 41.624532, -70.525567, 42.88653],
    '18': [144.333611, -38.497396, 145.878412, -37.176288],
    '19': [-141.003243, 41.676563, -52.621799, 83.212586],
    '20': [72.776216, 18.837244, 73.318742, 19.415803],
    '21': [-123.024066, 37.421464, -121.556419, 38.321209],
    '22': [-4.5749, 39.8757, -3.1943, 40.8887],
    '23': [54.851306, 24.820998, 55.663345, 25.60974],
    '24': [-76.233259, 39.29047, -74.389905, 40.60858],
    '25': [-118.367624, 14.53469, -86.709672, 32.718727],
    '26': [-85.05031, 33.180238, -83.51017, 34.617924],
    '27': [-2.6897, 54.7839, -1.3556, 55.8055],
    '28': [-97.202304, 32.057764, -95.449165, 33.95513],
    '29': [3.8391, 51.6068, 4.9269, 52.0656],
    '30': [-96.874222, 28.828439, -94.354163, 31.146578],
    '31': [-122.502534, 46.783645, -120.906211, 48.299232],
    '32': [106.369264, -6.824098, 107.240628, -6.008749],
    '33': [3.6596, 50.4958, 5.2439, 51.0499],
    '34': [73.239073, -54.762088, 167.982911, -10.061317],
    '35': [8.6645, 45.1418, 9.6652, 45.7706],
    '36': [34.571086, 16.379518, 55.666714, 32.154292],
    '37': [73.608963, 18.300691, 74.156847, 18.878544],
    '38': [-106.210173, 38.693551, -103.705701, 40.044227],
    '39': [-73.560364, -55.054609, -53.637487, -21.781045],
    '40': [-81.737731, -4.227113, -66.846315, 13.387995],
    '41': [1.5575, 41.186215, 2.6709, 41.7595],
    '43': [-18.160459, 27.637524, 4.328711, 43.792063],
    '44': [5.866776, 47.270236, 15.041917, 55.055431],
    '45': [79.882075, 12.656271, 80.331245, 13.293387],
    '46': [17.2449, 58.7937, 19.087517, 60.222911],
    '47': [-118.076307, 33.386629, -117.512231, 34.38863],
    '48': [-122.436882, 36.196848, -120.596562, 37.900133],
    '49': [5.7264, 52.062, 6.2179, 52.3984],
    '50': [54.300087, 24.234059, 54.677631, 24.59771],
    '51': [-113.333897, 32.501344, -110.448571, 34.04817],
    '52': [28.647059, 40.80514, 29.388213, 41.299],
    '53': [116.927102, 4.645415, 126.604556, 21.121996],
    '54': [6.627008, 36.649559, 18.5204, 47.092047],
    '55': [-94.26152, 44.471871, -92.135404, 46.159465],
    '56': [-74.555721, 45.002783, -72.237733, 46.449138],
    '57': [-74.453329, 3.727109, -73.791146, 4.985573],
    '58': [120.677053, 13.849342, 121.501829, 15.143122],
    '59': [-84.158189, 41.723547, -82.412965, 43.168946],
    '60': [120.929383, 30.689944, 121.912076, 31.612367],
    '61': [-77.186939, -12.46036, -76.544396, -11.726187],
    '62': [-97.391649, 26.066841, -96.397846, 28.371475],
    '63': [-117.59588, 32.534156, -116.08109, 33.505025],
    '64': [16.454494, -46.981858, 38.003708, -22.125431],
    '65': [113.90028, 22.195985, 114.312082, 22.504286],
    '66': [-123.297602, 48.973943, -122.409143, 49.574275],
    '67': [-98.59167, 29.630745, -97.024461, 30.906188],
    '68': [30.903761, 29.267575, 31.669519, 30.557135],
    '69': [5.191, 51.2208, 5.9312, 51.5982],
    '70': [89.857326, 23.420311, 90.726379, 24.074926],
    '71': [12.4343, 51.9794, 14.2588, 53.1539],
    '72': [-80.519425, 39.720018, -74.754709, 42.26986],
    '73': [25.664523, 35.812168, 44.818121, 42.104892],
    '74': [-7.5312, 52.5921, -5.994191, 53.9146],
    '75': [10.981359, 55.376231, 24.152344, 69.060159],
    '76': [-76.617109, 42.723844, -75.239547, 43.707018],
    '77': [11.8183, 41.4447, 13.3214, 42.4093],
    '78': [34.267262, 29.490669, 35.670344, 33.290833],
    '79': [19.9776, 51.8381, 22.1606, 52.8335],
    '80': [115.679593, 39.522886, 116.969933, 40.300859],
    '81': [2.83463, 6.394354, 3.806536, 6.994414],
    '82': [-7.662581, 54.557064, 15.160778, 62.395416],
    '83': [4.3822, 45.3736, 5.4546, 46.1282],
    '84': [101.269587, 2.782581, 101.915276, 3.429961],
    '85': [-109.455463, -55.915437, -66.424213, -17.498353],
    '86': [99.640383, 0.853916, 119.275424, 7.359587],
    '87': [24.696755, 21.724963, 35.793548, 31.67092],
    '88': [-71.277692, -34.289902, -69.769011, -32.9416],
    '89': [25.856, 44.2365, 26.4519, 44.6123],
    '90': [174.346198, -37.36808, 175.290927, -36.426903],
    '92': [-81.957641, 27.642288, -80.861398, 29.269717],
    '93': [-81.330049, -18.352305, -68.652331, -0.03899],
    '94': [10.8843, 47.7631, 12.3263, 48.6627],
    '95': [-82.851126, 27.612972, -82.054036, 28.694859],
    '96': [36.719375, 55.103805, 38.304308, 56.366996],
    '97': [-2.3255, 52.2781, -1.46, 52.8074],
    '98': [-123.486142, 44.885734, -121.514765, 46.388221],
    '99': [-91.512974, 36.970298, -87.495211, 42.508302],
    '100': [-44.419128, -20.169108, -43.673932, -19.531608],
    '101': [95.007064, -10.93168, 141.022142, 5.91625],
    '102': [2.663545, 4.270789, 14.677991, 13.885642],
    '103': [-81.357417, 24.714199, -81.288259, 24.76721],
    '104': [-9.500422, 38.409654, -8.491, 39.2555],
    '105': [-80.87319, 25.139115, -80.117904, 25.995083],
    '106': [-81.492385, 34.814076, -80.277052, 35.515103],
    '107': [5.955362, 45.818854, 10.490914, 47.809346],
    '108': [-2.7529, 52.9472, -1.6618, 53.7551],
    '109': [-73.029537, 41.260099, -72.099229, 42.03886],
    '110': [11.6372, 55.130387, 12.682533, 56.129585],
    '111': [-87.014533, 39.3383, -85.575869, 40.379524],
    '112': [73.499411, 18.16039, 134.775205, 53.5607],
    '113': [-43.913535, -23.079041, -43.014606, -22.320941],
    '114': [119.363725, -5.470456, 119.636176, -4.927725],
    '115': [-83.65333, 39.361566, -82.159161, 40.712423],
    '116': [-0.2273, 52.006, 0.5131, 52.5136],
    '117': [152.073393, -28.362872, 153.547097, -26.451923],
    '118': [-78.995054, 35.25644, -78.064603, 36.075733],
    '119': [-114.047134, 39.899146, -110.000708, 41.37354],
    '120': [138.768992, 35.099826, 140.525558, 36.262805],
    '121': [-2.5633, 53.5199, -0.9033, 54.2593],
    '122': [6.297, 46.4705, 6.9025, 46.742],
    '123': [-180.0, 41.185345, 180.0, 81.861633],
    '124': [115.560745, -32.801873, 116.415125, -31.455129],
    '125': [-85.630016, 45.572213, -85.487026, 45.760222],
    '126': [-82.348076, 40.988272, -81.002117, 41.854908],
    '127': [-47.528943, -23.071579, -46.857247, -22.671044],
    '128': [8.07, 49.8629, 9.5312, 50.4852],
    '129': [51.21635, 25.098063, 51.625637, 25.551958],
    '130': [-73.366353, 0.647671, -59.805781, 12.201326],
    '131': [60.872972, 23.693731, 77.013095, 37.083971],
    '132': [-77.426775, 38.786155, -76.060931, 39.721305],
    '133': [-80.519207, 39.952065, -79.688986, 40.67429],
    '134': [36.623131, -1.383045, 37.033202, -1.124215],
    '135': [-87.743122, 35.609851, -86.014849, 36.652486],
    '136': [-122.770045, 47.904023, -122.349597, 48.411565],
    '137': [18.4295, 47.0371, 20.0176, 48.0246],
    '138': [66.786674, 24.759563, 67.372665, 25.081264],
    '139': [-95.509014, 38.027337, -93.758463, 40.136626],
    '140': [87.934211, 22.187866, 88.79752, 23.130029],
    '141': [-9.086265, -54.46624, 33.632429, 80.757641],
    '142': [-10.484151, 51.444579, -6.102482, 55.382016],
    '143': [2.5627, 50.3421, 3.5196, 50.822097],
    '144': [-76.668626, 44.714672, -74.691971, 45.994787],
    '145': [-100.151361, 19.071491, -99.313366, 19.582075],
    '146': [-49.509003, -25.678155, -49.018347, -25.165266],
    '147': [-59.034545, -35.207538, -58.12226, -34.24037],
    '148': [126.520315, 37.01206, 127.849448, 38.00337],
    '149': [-85.140312, 38.473039, -83.903917, 39.591024],
    '150': [-51.338791, -30.29174, -50.83708, -29.7963],
    '151': [73.734469, 31.131387, 74.588203, 31.922064],
    '153': [20.261477, 43.619163, 29.714512, 48.265184],
    '154': [8.414394, 52.9321, 10.9716, 54.0138],
    '155': [-120.918731, 35.907186, -118.360586, 37.777986],
    '156': [13.7989, 49.5303, 15.2535, 50.4712],
    '157': [-78.830339, 37.192592, -76.645644, 38.161631],
    '158': [-122.422048, 38.018421, -119.879543, 39.31635],
    '159': [-88.541535, 42.841886, -87.793239, 43.543532],
    '160': [6.42275, 51.018, 7.0415, 51.36845],
    '161': [-1.4601, 51.1341, -0.9787, 51.3839],
    '162': [-85.605165, 30.568591, -80.885517, 35.000771],
    '163': [-114.994477, 50.30961, -112.236767, 51.940599],
    '164': [19.515755, 59.807915, 31.587079, 70.091852],
    '165': [-120.248484, 33.895712, -119.97026, 34.03734],
    '166': [22.137179, 44.382045, 40.220451, 52.37937],
    '167': [-99.533409, 19.089378, -98.628055, 19.902961],
    '168': [-84.44408, 9.747509, -83.784989, 10.121701],
    '169': [0.7001, 43.0954, 1.9746, 43.9683],
    '170': [-2.9946, 51.2906, -2.2558, 51.677112],
    '171': [-1.7195, 51.5187, -1.0552, 52.1658],
    '172': [-67.146035, 10.235618, -66.635824, 10.553042],
    '173': [-73.365771, 9.185621, 7.226909, 53.547293],
    '174': [4.8186, 43.14453, 6.0262, 43.8204],
    '175': [19.373786, 34.802025, 28.248685, 41.750252],
    '176': [72.35816, 22.829536, 72.79924, 23.288725],
    '177': [-99.60313, 28.612658, -97.633176, 30.138954],
    '178': [9.9453, 59.3054, 11.9399, 60.6051],
    '180': [7.1863, 46.689, 7.8416, 47.1229],
    '181': [14.122921, 49.00237, 24.14576, 54.837071],
    '182': [15.6236, 47.6751, 17.1608, 48.877636],
    '183': [-178.927793, -52.612077, 179.069538, -8.538779],
    '184': [-13.172306, 27.667267, -0.99699, 35.922329],
    '185': [-71.862772, 41.146339, -71.224798, 42.018798],
    '186': [-80.046709, 35.899627, -79.532365, 36.257351],
    '187': [-90.791562, 46.753034, -90.568874, 46.874466],
    '188': [-4.8964, 55.2915, -3.397, 56.0746],
    '189': [-65.231933, 18.310464, -65.221109, 18.323013],
    '190': [35.414529, 33.649942, 35.735144, 34.27515],
    '191': [100.028069, 13.407807, 101.008341, 14.258456],
    '192': [-3.205807, 53.299, -2.5784, 53.732086],
    '193': [11.478925, 57.3001, 12.764, 58.3081],
    '194': [-118.196274, 35.002085, -114.045806, 39.16343],
    '195': [-118.601185, 33.299075, -118.016722, 33.93859],
    '196': [6.4583, 50.6893, 7.5142, 51.1381],
    '197': [-8.7275, 40.9648, -8.1692, 41.2952],
    '199': [-79.761951, 41.426746, -73.242042, 45.01585],
    '200': [-2.0649, 46.9227, -1.1616, 47.6503],
    '201': [-31.267132, 32.403066, -6.188604, 42.153926],
    '202': [-80.8831, 25.901354, -80.046735, 26.528413],
    '203': [122.933183, 24.255137, 153.987069, 45.55625],
    '204': [121.027366, 24.762257, 121.888449, 25.258725],
    '205': [23.7526, 59.943375, 25.9501, 60.8409],
    '206': [50.759491, 35.347304, 51.760955, 35.968624],
    '207': [-3.8312, 55.7116, -2.367157, 56.065425],
    '208': [-92.231131, 13.740226, -88.233003, 17.815705],
    '209': [106.331167, 10.439081, 107.118807, 11.188271],
    '210': [113.903566, 22.50458, 114.493815, 22.929672],
    '211': [10.9909, 44.1996, 11.7752, 44.7786],
    '212': [8.574, 49.27445, 8.943, 49.52055],
    '213': [-1.261149, 44.3351, -0.1189, 45.2501],
    '214': [12.036, 45.1045, 12.5854, 45.6098],
    '215': [23.551234, 37.6516, 24.08624, 38.341249],
    '216': [138.445414, -35.350296, 139.04403, -34.500225],
    '217': [-82.459792, 29.622432, -81.212878, 31.169595],
    '218': [20.198157, 44.583505, 20.728356, 45.00696],
    '219': [30.031988, 50.062352, 31.026236, 50.800097],
    '220': [-1.0671, 39.2413, -0.26675, 39.7821],
    '221': [-76.04653, 37.915008, -75.994739, 37.953586],
    '222': [9.530849, 46.372166, 17.160804, 49.020668],
    '223': [-95.774704, 35.995812, -89.100766, 40.61364],
    '224': [-76.949066, 36.07171, -75.773329, 36.968747],
    '225': [102.143932, 8.561276, 109.467107, 23.392644],
    '226': [46.553018, 28.524446, 48.654036, 30.103691],
    '227': [7.522313, 30.228029, 11.599218, 37.542316],
    '228': [-8.667611, 18.968136, 11.99848, 37.089805],
    '229': [72.821634, 33.415931, 73.293464, 33.771375],
    '231': [-0.8206, 52.887, -0.1422, 53.6143],
    '232': [-98.576256, 32.052087, -96.929128, 33.916064],
    '233': [33.909829, -4.679897, 41.906935, 5.033423],
    '234': [-130.107894, -59.485448, 72.494561, 60.847081],
    '235': [-1.571, 52.3929, -0.7176, 52.8243],
    '236': [-97.239155, 43.499356, -89.489226, 49.384358],
    '237': [32.361216, 39.373243, 33.119633, 40.189404],
    '238': [-100.861901, 25.223786, -99.766613, 26.395167],
    '239': [5.7361, 49.447234, 6.530899, 50.1828],
    '240': [-89.839208, 42.845046, -89.004686, 43.643373],
    '241': [-91.662938, -5.016165, -75.187149, 1.453209],
    '242': [-91.418637, 38.003499, -89.595103, 39.399882],
    '243': [12.813691, 55.337608, 13.6638, 55.8461],
    '244': [-88.071449, 37.776843, -84.785305, 41.760592],
    '245': [-48.219482, -15.963516, -47.811955, -15.618798],
    '246': [-115.33928, 52.929922, -111.405284, 54.152162],
    '247': [79.65203, 5.914561, 81.880372, 9.832086],
    '248': [-78.3696, 17.705396, -76.184654, 18.525432],
    '249': [-105.330444, 38.519569, -104.051589, 39.129685],
    '250': [76.548751, 30.548574, 76.961258, 30.848384],
    '251': [10.9714, 43.5182, 11.7189, 44.0837],
    '252': [13.9027, 40.6179, 14.7185, 41.0897],
    '253': [-7.949677, 33.407269, -7.258755, 33.754492],
    '254': [107.231505, -7.229651, 108.096071, -6.670022],
    '255': [-98.31337, 34.854632, -96.619181, 36.16489],
    '256': [-35.150883, -8.260403, -34.82175, -7.740849],
    '257': [-103.843526, 20.325773, -103.081301, 20.995975],
    '258': [-96.639704, 40.378264, -90.140613, 43.50055],
    '259': [14.182888, 35.80616, 14.576955, 36.082096],
    '260': [-0.3715, 50.7557, 0.1489, 51.0002],
    '261': [7.3757, 47.3673, 7.9565, 47.597],
    '262': [-1.312548, 50.77125, -0.9293, 50.9091],
    '263': [-74.763303, 42.096589, -73.264957, 43.397988],
    '264': [-86.681424, 37.527187, -84.866981, 38.828454],
    '265': [35.674068, 31.715556, 36.27623, 32.223537],
    '266': [3.3421, 43.3895, 4.2393, 43.9621],
    '267': [-38.833374, -4.077509, -38.330768, -3.608019],
    '268': [0.065409, 49.3477, 0.6338, 49.7383],
    '269': [-2.287, 47.7257, -1.2538, 48.4653],
    '270': [6.4167, 52.1182, 7.072354, 52.4695],
    '271': [-75.71934, 5.978637, -75.402708, 6.436417],
    '272': [76.037174, 9.755642, 76.573384, 10.536761],
    '273': [-3.260803, 4.73909, 1.199465, 11.174946],
    '274': [-94.042964, 29.03658, -89.568498, 33.019219],
    '275': [-38.538202, -13.018181, -38.174192, -12.705258],
    '276': [19.5253, 49.731, 20.6689, 50.4275],
    '277': [-6.3307, 36.8937, -5.3981, 37.7989],
    '278': [-80.901014, 43.110332, -80.187603, 43.845178],
    '279': [-3.6419, 51.380399, -3.0676, 51.8283],
    '280': [-1.8227, 53.1047, -1.1159, 53.6116],
    '281': [97.343877, 5.612854, 105.636791, 20.465102],
    '282': [-85.790448, 42.7682, -84.835305, 43.468314],
    '283': [39.009723, 21.22276, 39.516146, 21.904586],
    '284': [105.342699, 20.649958, 106.321922, 21.397258],
    '285': [-78.466429, 42.471216, -77.371478, 43.376505],
    '286': [-90.506475, 34.495862, -89.017127, 35.649995],
    '287': [44.032719, 25.060401, 63.333348, 39.782204],
    '288': [5.6391, 50.755158, 5.8852, 50.9357],
    '289': [-79.706768, 8.886926, -79.082138, 9.267801],
    '290': [-80.248486, 42.788746, -79.386171, 43.471036],
    '291': [-82.764464, 34.484002, -82.146792, 35.215395],
    '292': [-94.617919, 33.004106, -89.686924, 36.499463],
    '293': [50.310066, 25.556807, 50.824874, 26.331334],
    '294': [27.894555, -25.888597, 28.539606, -25.551958],
    '295': [-78.596651, -0.53379, -78.265665, 0.113228],
    '296': [-81.571899, 33.466006, -80.283998, 34.614765],
    '297': [7.0408, 48.2573, 8.042999, 48.9045],
    '298': [-46.162792, -23.42242, -45.684508, -23.029836],
    '299': [-90.68885, 28.925011, -88.817017, 30.711972],
    '300': [-88.471214, 30.221132, -84.909052, 35.007712],
    '301': [-89.559529, 13.448677, -88.850926, 14.045681],
    '302': [124.613755, 33.194576, 130.931229, 38.618277],
    '303': [16.113811, 45.737046, 22.896561, 48.585338],
    '304': [-94.744876, 40.89667, -93.327886, 41.863728],
    '305': [22.7484, 42.168, 24.0587, 43.1614],
    '306': [-84.321869, 33.844517, -75.533012, 36.588117],
    '307': [-68.518703, 44.099722, -68.491521, 44.127237],
    '308': [-79.136725, 42.440807, -78.460416, 43.374763],
    '309': [16.566099, 50.7947, 17.446, 51.4611],
    '310': [6.7816, 43.61813, 7.4478, 44.1956],
    '311': [-96.909107, 40.580501, -95.154722, 41.866306],
    '312': [-0.570341, 5.446151, 0.09008, 5.867498],
    '313': [75.620265, 26.689588, 75.986093, 27.086654],
    '314': [-84.485367, 39.550254, -83.64661, 39.92297],
    '315': [46.493677, 40.568698, 87.315395, 55.441975],
    '316': [-82.843602, 41.628323, -82.793069, 41.665973],
    '317': [-72.00752, 17.537908, -68.325398, 19.933788],
    '318': [-72.556214, 42.69699, -70.703936, 45.305293],
    '319': [120.35298, 23.765249, 120.813792, 24.376506],
    '320': [5.7731, 48.8309, 6.6037, 49.2793],
    '321': [174.612898, -41.436482, 175.508194, -40.923497],
    '322': [-49.54354, -17.015506, -49.1354, -16.415275],
    '323': [-84.865138, 37.472663, -83.63106, 38.492917],
    '324': [-97.064107, 35.37669, -95.00573, 36.999211],
    '325': [26.797218, 38.258267, 27.333503, 38.54963],
    '326': [-80.886013, 26.454049, -80.031362, 27.263723],
    '327': [-113.333897, 31.426132, -110.447586, 32.513807],
    '328': [-47.690416, -23.731787, -47.341236, -23.330499],
    '329': [-47.003073, -24.322911, -46.164236, -23.848917],
    '330': [-87.636118, 33.019257, -86.044363, 34.260185],
    '331': [112.366829, -8.268523, 112.966058, -7.067414],
    '332': [5.0806, 50.2217, 5.931, 50.8119],
    '333': [60.5176, 29.37728, 74.889863, 38.490737],
    '334': [18.2353, 49.9521, 19.646, 50.6651],
    '335': [25.237449, -22.4219, 33.068222, -15.609357],
    '336': [-4.9391, 36.57157, -4.2311, 36.9809],
    '337': [30.645937, -30.223462, 31.135074, -29.591508],
    '338': [10.7545, 45.2919, 11.2111, 45.6071],
    '339': [-6.4961, 54.3381, -5.4341, 54.8119],
    '340': [-3.0207, 42.6376, -2.3307, 43.0519],
    '341': [-84.272889, 35.795196, -83.654605, 36.435679],
    '342': [-48.831818, -27.840438, -48.414568, -27.365764],
    '343': [-116.049193, 44.371038, -104.040114, 49.001076],
    '344': [79.812287, 6.37814, 80.236328, 7.448708],
    '345': [-80.790296, 32.493564, -79.268169, 33.507171],
    '346': [43.960008, 32.896756, 44.712904, 33.641271],
    '347': [-0.6564, 38.2749, -0.3032, 38.5367],
    '348': [73.045175, 22.146015, 73.297103, 22.479279],
    '349': [75.649215, 22.49595, 76.028976, 22.837879],
    '350': [29.571452, 30.908389, 30.256799, 31.332269],
    '351': [34.955479, 29.185039, 39.300872, 33.374726],
    '352': [80.748149, 26.647378, 81.337695, 27.044388],
    '353': [9.1591, 52.0662, 10.2957, 52.7601],
    '354': [-114.813613, 31.332502, -109.045223, 37.003875],
    '355': [-40.59207, -20.52508, -40.179209, -20.094545],
    '356': [-117.026922, 41.996012, -114.967224, 44.504345],
    '357': [22.357165, 41.235349, 28.610283, 44.215451],
    '358': [0.9471, 52.3554, 1.6821, 52.8306],
    '359': [7.111767, 8.902092, 7.720502, 9.316573],
    '360': [29.340016, -11.761201, 40.444602, -0.984407],
    '361': [-124.552441, 41.992671, -116.463504, 46.269531],
    '362': [76.832672, 10.772871, 77.208119, 11.171976],
    '363': [15.3294, 45.4277, 16.6953, 46.2106],
    '364': [12.091589, 48.55192, 18.859246, 51.054907],
    '365': [-82.644739, 37.202467, -77.739204, 40.638801],
    '366': [49.874882, 26.132888, 50.23411, 26.714917],
    '367': [32.267907, 34.632302, 34.59415, 35.697091],
    '368': [-106.195372, 40.257884, -104.94311, 40.998084],
    '369': [-91.822372, 30.025681, -90.401254, 31.044794],
    '370': [148.762675, -35.92041, 149.39718, -35.126389],
    '371': [92.171969, 9.782952, 101.170163, 28.547834],
    '372': [-93.075178, 34.413147, -91.633263, 35.07397],
    '373': [-102.051744, 36.993083, -94.613981, 40.003078],
    '374': [-83.883383, 41.165751, -83.165713, 41.732647],
    '375': [-60.112155, -3.179083, -59.880177, -2.903953],
    '376': [-3.8016, 56.7479, -1.767523, 57.702072],
    '377': [-47.887599, -21.330773, -47.584182, -21.006823],
    '378': [-49.274617, -27.019031, -48.890835, -26.740248],
    '379': [-64.409908, -31.646759, -63.936475, -31.097067],
    '380': [-17.528224, 14.64649, -17.182583, 14.830155],
    '381': [-69.64491, -22.898097, -57.453875, -9.669312],
    '382': [-114.052718, 36.998216, -109.041723, 42.001567],
    '383': [-1.046, 47.2072, -0.0925, 47.7584],
    '384': [-84.932519, 29.967106, -83.604186, 30.711542],
    '385': [-83.166091, 28.993087, -82.049972, 29.945207],
    '386': [16.3368, 52.0656, 17.5235, 52.7596],
    '387': [20.95368, 53.896794, 26.835527, 56.450256],
    '388': [-80.088914, -2.289046, -79.679501, -1.973543],
    '389': [9.9832, 45.3903, 10.4076, 45.6633],
    '390': [-2.3255, 50.575384, -1.6818, 50.9919],
    '391': [-76.786584, 3.091813, -76.280355, 3.699444],
    '392': [105.342699, 20.649958, 106.321922, 21.397258],
    '393': [-98.014513, 49.00001, -95.809706, 50.816693],
    '394': [-56.433232, -34.936588, -55.80941, -34.614534],
    '395': [17.9189, 54.1036, 19.2125, 54.6997],
    '396': [-64.674581, 43.922856, -59.710612, 45.336301],
    '397': [0.1809, 46.9989, 1.1621, 47.7093],
    '398': [9.944963, 36.618159, 10.471018, 36.987729],
    '399': [0.775392, 6.23479, 3.84261, 12.408613],
    '400': [-47.785444, -22.854567, -47.528943, -22.57098],
    '401': [-8.6653, 43.1191, -8.063, 43.406215],
    '402': [-89.356355, 12.980819, -83.149725, 17.418734],
    '403': [49.470146, 40.247513, 50.125741, 40.713553],
    '404': [8.5738, 44.3148, 9.3303, 44.6762],
    '405': [-89.156738, 30.202305, -89.063989, 30.250199],
    '406': [-86.790056, 34.475652, -86.255771, 34.99207],
    '407': [-104.048736, 45.935238, -96.554835, 49.000687],
    '408': [30.815024, -18.04502, 31.287736, -17.608234],
    '409': [-117.782448, 33.426931, -116.697283, 34.825335],
    '410': [-73.437429, 42.726853, -71.464555, 45.016659],
    '411': [-120.005746, 38.414677, -118.753835, 41.997544],
    '412': [-4.251796, 5.220414, -3.791632, 5.52717],
    '413': [-109.046156, 34.259715, -105.290299, 36.219799],
    '415': [-97.807823, 36.998713, -96.522782, 38.087704],
    '416': [16.1566, 48.8513, 17.1766, 49.5294],
    '417': [38.571826, 8.820842, 38.987715, 9.113373],
    '419': [-62.63895, -27.606759, -54.258571, -19.287666],
    '420': [-158.198074, 21.2577, -157.664876, 21.629358],
    '421': [-124.158684, 43.437142, -121.767375, 44.290548],
    '422': [76.728724, 8.317245, 77.182579, 8.804593],
    '423': [-85.863935, 34.581799, -84.94233, 35.567422],
    '424': [8.2444, 51.8541, 8.8195, 52.1867],
    '425': [24.3937, 54.3934, 25.762648, 55.0463],
    '426': [-81.835519, 42.472608, -80.805211, 43.301044],
    '427': [-121.584074, 37.481783, -120.920665, 38.300252],
    '428': [-85.686574, 40.916585, -84.802936, 41.295241],
    '429': [8.49877, 1.655903, 16.194425, 13.083333],
    '430': [-3.9255, 50.4611, -2.8892, 51.0338],
    '431': [-48.990071, -26.461724, -48.701344, -26.107608],
    '432': [13.490327, 42.39123, 19.447234, 46.555022],
    '433': [-48.424186, -19.002468, -48.147617, -18.853764],
    '434': [13.375587, 45.421436, 16.610168, 46.876666],
    '435': [-109.060062, 36.992426, -102.041574, 41.003444],
    '436': [88.008628, 20.725397, 92.680104, 26.63391],
    '437': [16.9348, 47.731362, 22.565731, 49.613729],
    '438': [119.85517, 29.809105, 120.76663, 30.625635],
    '439': [6.2962, 51.2232, 8.08115, 51.8217],
    '440': [27.194297, 53.618754, 28.004932, 54.032598],
    '441': [-86.506502, 11.954514, -86.122135, 12.280824],
    '442': [56.584904, -20.525554, 63.502816, -10.336484],
    '443': [16.8339, 48.0067, 17.5237, 48.654],
    '444': [-75.072845, 10.490808, -74.715497, 11.082487],
    '445': [4.4892, 46.9994, 5.5175, 47.7093],
    '446': [-1.2094, 41.3336, -0.4644, 42.015],
    '447': [123.528431, 9.950854, 124.043727, 10.671066],
    '448': [3.5157, 49.0441, 4.6529, 49.6781],
    '449': [-104.057698, 42.479846, -96.436589, 45.94531],
    '450': [58.287712, 23.506005, 58.614707, 23.644547],
    '451': [-98.425952, 18.719129, -97.746899, 19.287976],
    '452': [-118.979519, 47.259272, -117.032351, 48.999957],
    '453': [20.969131, 55.674661, 28.241385, 58.085562],
    '454': [-81.780858, 31.720934, -80.840549, 32.595383],
    '455': [78.880798, 20.990218, 79.246736, 21.347393],
    '456': [-48.495914, -1.480108, -48.170342, -1.229363],
    '457': [-61.933714, 10.041136, -60.519346, 11.348467],
    '458': [-90.076823, 34.987669, -81.6469, 36.678118],
    '459': [21.827444, 57.509359, 28.210036, 59.637894],
    '460': [-117.214889, 41.988057, -111.043924, 49.000912],
    '461': [16.471699, 40.8288, 17.1448, 41.2294],
    '462': [14.8419, 37.3587, 15.1662, 37.739],
    '463': [-58.440587, -34.97738, -53.077938, -30.085315],
    '464': [-79.457919, 34.833377, -78.49461, 35.266387],
    '465': [-106.645296, 30.629363, -104.907122, 32.002533],
    '466': [-90.93214, 31.70128, -89.47423, 32.8913],
    '467': [8.388, 52.7541, 9.2935, 53.5973],
    '468': [-111.056888, 40.994746, -104.052583, 45.005904],
    '469': [19.999169, -26.907534, 29.375307, -17.778262],
    '470': [2.5859, 45.5112, 3.5159, 46.1218],
    '471': [-89.571509, 36.497692, -89.465445, 36.581147],
    '472': [12.9345, 37.7777, 13.637, 38.226247],
    '473': [29.573438, -1.482328, 35.033047, 4.218626],
    '474': [-109.050044, 31.332502, -103.002199, 37.000104],
    '475': [72.624875, 21.081557, 73.025965, 21.347393],
    '476': [-4.3351, 50.2015, -3.480878, 50.8734],
    '477': [-70.179108, 18.350192, -69.462868, 18.655569],
    '478': [51.999998, 16.650091, 59.837936, 26.386328],
    '479': [-83.352485, 32.034599, -78.541087, 35.196044],
    '480': [-103.002434, 33.615765, -94.431215, 37.002133],
    '481': [-97.368688, 40.349718, -96.463632, 41.046947],
    '482': [35.587222, 32.311129, 42.376331, 37.320505],
    '483': [-51.375195, -23.506005, -50.967621, -23.188487],
    '484': [2.599552, 36.503904, 3.641843, 36.820126],
    '485': [19.288738, 39.644484, 21.057269, 42.661158],
    '486': [32.408035, 0.03235, 32.857265, 0.485263],
    '487': [2.54343, 49.496642, 6.40756, 51.505134],
    '488': [12.991731, -9.162137, 13.58397, -8.650241],
    '489': [11.9701, 51.0642, 12.9287, 51.628],
    '490': [3.670697, 7.205314, 4.101814, 7.635345],
    '491': [85.728514, 20.169108, 85.91884, 20.417752],
    '492': [85.140507, 27.560452, 85.511033, 27.797642],
    '493': [-94.083833, 36.794011, -92.088708, 38.072543],
    '494': [-51.363768, -29.267575, -51.072975, -29.020624],
    '495': [-24.5276, 63.390364, -13.490864, 66.540413],
    '496': [119.990228, 30.994134, 121.127538, 31.870421],
    '497': [28.128857, -15.585979, 28.502326, -15.225105],
    '498': [109.881021, -7.351344, 110.756083, -6.840317],
    '499': [72.776216, 18.837244, 73.318742, 19.415803],
    '500': [2.34624, 39.326, 3.166, 39.8335],
    '501': [-7.085611, 33.823438, -6.636357, 34.17952],
    '502': [-88.432007, 30.230274, -87.92429, 31.173306],
    '503': [75.67802, 30.762688, 76.097712, 31.02844],
    '504': [103.591375, 30.308978, 104.388666, 31.054173],
    '505': [77.931641, 30.189263, 78.146824, 30.403079],
    '506': [-59.652659, 13.044825, -59.421656, 13.335484],
    '507': [140.841971, -11.657967, 159.493748, -1.119619],
    '509': [-8.602045, 4.360392, -2.493047, 10.740013],
    '510': [-104.053249, 39.999998, -95.30829, 43.000585],
    '511': [84.931685, 25.509903, 85.293404, 25.661326],
    '512': [77.248351, 23.113328, 77.534507, 23.372278],
    '514': [-78.029922, 33.928645, -77.715916, 34.389359],
    '515': [9.544132, 3.964175, 9.832887, 4.174658],
    '516': [44.690764, 41.614049, 44.970144, 41.830863],
    '517': [6.920023, 4.725257, 7.119318, 4.911526],
    '518': [151.396164, -33.177093, 152.043696, -32.57885],
    '519': [-87.09411, 5.498621, -82.553005, 11.219686],
    '520': [-61.037773, -33.173506, -60.486954, -32.577171],
    '521': [78.591988, 10.70773, 78.900103, 10.895024],
    '522': [40.994388, -1.662032, 51.415011, 11.988726],
    '523': [13.3798, 50.7738, 14.1631, 51.4145],
    '524': [-5.51324, 9.410461, 2.404327, 15.08403],
    '525': [-176.211855, 51.967825, -175.988653, 52.116097],
    '526': [118.549702, 31.672556, 119.093155, 32.456359],
    '527': [32.99773, 3.404126, 48.001064, 14.894221],
    '528': [-49.146084, -22.454274, -48.891872, -22.262623],
    '529': [-73.727775, 40.985697, -71.789356, 42.049638],
    '530': [80.058457, 26.347373, 88.20125, 30.447382],
    '531': [38.793678, 29.061198, 48.611579, 37.380663],
    '532': [43.220312, -25.605466, 50.486878, -11.9523],
    '533': [-49.526949, -20.915501, -49.231482, -20.649958],
    '534': [-156.69989, 20.578427, -155.984587, 21.034371],
    '535': [21.814769, 9.488892, 38.582246, 23.146403],
    '536': [26.616436, 45.467441, 30.163575, 48.491986],
    '537': [-17.289552, 12.307291, -11.346136, 16.692969],
    '538': [44.287706, 39.987483, 44.649231, 40.30982],
    '539': [-52.111017, -23.531085, -51.778325, -23.330499],
    '540': [-64.409908, -31.646759, -63.936475, -31.097067],
    '541': [-35.020379, -7.302666, -34.795864, -6.969043],
    '542': [44.226685, 38.392221, 50.621817, 41.912352],
    '543': [-74.482943, 18.020411, -71.621738, 20.08839],
    '544': [-43.492576, -21.879619, -43.269591, -21.58015],
    '545': [83.056621, 17.583522, 83.36357, 17.855421],
    '546': [-76.095548, 38.07076, -76.005904, 38.203731],
    '547': [-75.422099, 38.928519, -74.302184, 40.922842],
    '548': [15.728746, 42.555466, 19.623643, 45.276541],
    '549': [-44.380478, -2.765033, -44.025419, -2.412923],
    '550': [-35.375435, -6.021482, -35.114198, -5.632502],
    '551': [-16.5377, 28.1989, -16.1198, 28.588477],
    '552': [102.333553, 10.40454, 107.627681, 14.690013],
    '553': [-79.307093, 20.912101, -72.727893, 27.238715],
    '554': [11.737516, -28.970374, 25.261768, -16.963489],
    '555': [104.692371, 11.41643, 105.02933, 11.742494],
    '556': [-97.942146, 27.558358, -97.04598, 28.176848],
    '557': [20.452442, 40.853944, 23.034052, 42.373652],
    '558': [11.670472, -18.039102, 24.087897, -4.388072],
    '559': [-84.952069, 19.826259, -74.133796, 23.221954],
    '560': [23.178216, 51.262021, 32.776803, 56.172458],
    '561': [72.700395, -0.702935, 73.745417, 7.108752],
    '562': [-150.020578, 61.066015, -149.444084, 61.309992],
    '563': [73.626664, 19.862655, 73.926879, 20.102829],
    '564': [28.861798, -2.839936, 30.89913, -1.047619],
    '566': [-54.865201, -20.666549, -54.516593, -20.368011],
    '567': [-120.194146, 34.790629, -117.616195, 35.798149],
    '568': [-102.615447, 32.958831, -101.038788, 34.313258],
    '569': [7.408962, 43.724606, 7.43964, 43.751768],
    '570': [-117.123927, 32.185218, -116.524658, 32.570946],
    '571': [30.215616, -26.868157, 40.838087, -10.473185],
    '572': [113.505917, 22.862911, 114.29973, 23.388991],
    '573': [11.410251, 3.713241, 11.649442, 3.996555],
    '574': [21.99933, -18.077412, 33.709012, -8.203281],
    '575': [-75.766693, 38.451263, -75.048939, 39.377537],
    '576': [32.672856, -17.129512, 35.918589, -9.367232],
    '578': [81.475247, 21.156305, 81.774823, 21.413881],
    '579': [98.372281, 3.340937, 98.958712, 3.796256],
    '580': [113.876549, 30.266217, 114.656535, 30.788394],
    '581': [-87.693608, 10.70806, -82.733803, 15.029731],
    '582': [32.294106, 15.323502, 32.699395, 15.88142],
    '583': [-56.194141, -15.741886, -55.935561, -15.528552],
    '584': [11.152081, -5.040719, 18.650188, 3.707794],
    '586': [-70.275526, 41.237964, -69.960181, 41.391702],
    '587': [20.014262, 41.857731, 21.789869, 43.267676],
    '588': [-37.197684, -11.128241, -36.945496, -10.7973],
    '589': [72.830662, 31.277301, 73.558153, 31.646759],
    '590': [55.998215, 37.172258, 73.148607, 45.590066],
    '591': [91.422408, 26.048628, 91.898126, 26.234031],
    '592': [25.348913, -34.014436, 25.675708, -33.719325],
    '593': [-12.240366, 10.147821, 4.266683, 25.000665],
    '594': [32.384324, -26.031779, 32.721672, -25.703401],
    '595': [75.185789, 19.779869, 75.465827, 19.962021],
    '596': [76.486595, 12.207391, 76.732387, 12.427713],
    '597': [-180.0, -20.673776, 180.0, -12.478773],
    '598': [41.814856, 12.108545, 54.542913, 18.999643],
    '599': [-124.05517, 48.309497, -123.264729, 48.719853],
    '600': [-35.991672, -9.780041, -35.594946, -9.389737],
    '601': [80.022708, 26.284614, 80.488292, 26.638936],
    '602': [103.890025, 1.002891, 104.130528, 1.188942],
    '603': [9.391477, 19.499997, 25.149267, 33.166471],
    '604': [51.533648, 22.631524, 56.381578, 26.069401],
    '605': [71.307476, 33.762699, 72.314801, 34.327329],
    '606': [8.699632, -3.961803, 14.52693, 2.318109],
    '607': [34.880284, 31.342721, 35.574055, 32.552082],
    '608': [117.731724, 24.309465, 118.343005, 24.820998],
    '609': [-93.250883, 17.626032, -92.583168, 18.40346],
    '610': [108.627123, 33.988381, 109.298672, 34.553581],
    '611': [70.703452, 22.195985, 70.929885, 22.370935],
    '612': [119.793708, 36.042023, 120.734578, 36.582997],
    '613': [85.179419, 23.213544, 85.450381, 23.46421],
    '614': [-90.386649, 20.124716, -88.572942, 21.337973],
    '615': [15.15827, -4.53091, 15.513177, -4.2961],
    '617': [114.075928, 4.003145, 115.364449, 5.049562],
    '618': [116.710385, 20.697589, 122.006662, 26.38392],
    '619': [86.060474, 22.721087, 86.286092, 22.8796],
    '620': [46.376398, 24.384887, 47.086771, 25.005682],
    '621': [-11.498899, 4.353267, -7.369326, 8.551991],
    '622': [121.304385, 29.668287, 121.97836, 30.049278],
    '623': [18.838063, 42.231513, 23.006375, 46.189408],
    '624': [105.342699, 20.649958, 106.321922, 21.397258],
    '625': [74.147183, 16.56321, 74.352778, 16.842763],
    '626': [-0.144052, 6.112265, 1.808905, 11.139467],
    '627': [-15.076805, 7.190907, -7.638084, 12.674866],
    '628': [78.012914, 9.820708, 78.261942, 10.015934],
    '629': [73.629098, 24.502245, 73.834159, 24.711916],
    '630': [26.126421, -29.233499, 26.305342, -29.037647],
    '631': [75.46749, 31.225792, 75.701472, 31.457665],
    '632': [-42.995976, -5.308428, -42.597887, -4.903427],
    '633': [18.433561, 41.868755, 20.352929, 43.55821],
    '634': [74.758987, 32.577171, 75.004794, 32.82762],
    '635': [-13.302094, 6.923537, -10.271734, 9.999972],
    '636': [121.334884, 38.811214, 122.007598, 39.26429],
    '637': [-120.00532, 35.002085, -114.039882, 42.000252],
    '638': [75.837075, 10.525251, 76.476796, 11.114949],
    '639': [87.734497, 41.581845, 119.931531, 52.148347],
    '640': [82.730989, 25.089664, 83.309676, 25.518314],
    '641': [80.506621, 16.365974, 80.772323, 16.57965],
    '642': [104.521337, -3.106252, 104.892926, -2.823038],
    '643': [-112.613037, 28.227462, -110.382133, 29.712761],
    '644': [79.419125, 11.514234, 79.563442, 11.628355],
    '645': [113.095949, 34.518758, 113.948426, 35.024232],
    '646': [-93.24762, 16.645164, -93.033908, 16.844728],
    '647': [-101.50893, 19.453844, -100.878665, 19.893309],
    '648': [100.083983, 13.909999, 107.635115, 22.509049],
    '649': [12.202343, -13.459023, 31.314605, 5.391937],
    '650': [-39.060234, -12.444036, -38.812781, -12.076862],
    '651': [113.277432, 22.061081, 113.62279, 22.470944],
    '652': [112.71113, 27.967183, 113.234973, 28.408464],
    '653': [75.668398, 11.009051, 76.051385, 11.481631],
    '654': [77.804255, 26.993676, 78.127555, 27.306536],
    '655': [69.251112, 39.180453, 80.228149, 43.265367],
    '656': [71.257008, 30.001251, 71.713926, 30.377412],
    '657': [-61.41492, 1.164735, -56.491123, 8.520956],
    '658': [-83.052138, 7.203619, -77.158468, 9.633563],
    '659': [119.990228, 30.994134, 121.127538, 31.870421],
    '660': [122.996797, 41.541838, 123.651425, 42.047952],
    '662': [-61.46087, 12.583972, -61.119605, 13.380422],
    '663': [72.918391, 26.116034, 73.166932, 26.41111],
    '665': [116.996521, 31.560789, 117.673147, 32.137363],
    '667': [119.458119, 29.012112, 119.811382, 29.199428],
    '670': [78.019067, 11.522385, 78.419094, 11.807724],
    '671': [69.99639, 22.395935, 70.162712, 22.504286],
    '672': [77.506849, 28.867456, 77.847848, 29.148329],
    '673': [81.675855, 25.299714, 81.985827, 25.509903],
    '674': [13.469994, 7.442984, 23.999984, 23.452355],
    '675': [77.59913, 11.253452, 77.929029, 11.530536],
    '677': [23.440838, 3.488987, 35.948998, 12.236383],
    '678': [-89.227597, 15.885694, -87.767056, 18.495912],
    '679': [116.630655, 36.380931, 117.38308, 36.873267],
    '681': [-58.070525, 1.837294, -53.978752, 6.015511],
    '682': [-17.068726, 14.721271, -4.834114, 27.31588],
    '683': [-16.813628, 13.063719, -13.791419, 13.825038],
    '684': [59.373581, 36.135203, 59.772376, 36.495118],
    '685': [91.578867, 22.129359, 92.080047, 22.74611],
    '686': [119.034554, 25.804403, 119.719561, 26.234031],
    '687': [74.686178, 31.52641, 75.059507, 31.749962],
    '688': [78.099424, 26.132888, 78.251893, 26.309909],
    '689': [-25.360923, 14.807026, -22.667439, 17.204446],
    '690': [79.811366, 23.071579, 80.056512, 23.305434],
    '694': [119.961434, 33.208123, 120.325806, 33.563244],
    '695': [77.993162, 27.823066, 78.210496, 28.035027],
    '697': [46.208215, -10.152411, 56.294471, -3.79794],
    '698': [-61.081269, 13.704747, -60.873286, 14.108422],
    '699': [30.79066, -27.317391, 32.134894, -25.717911],
    '700': [119.708278, 31.517816, 120.193464, 31.990939],
    '701': [52.445568, 35.129081, 66.707122, 42.798257],
    '703': [102.543461, 24.787431, 102.978388, 25.24929],
    '704': [75.75695, 25.04767, 75.960054, 25.266097],
    '705': [-90.134636, 13.153, -87.690293, 14.450549],
    '706': [78.523227, 28.731379, 78.946381, 28.927011],
    '707': [0.16219, 11.693747, 15.999026, 23.514996],
    '708': [76.577212, 27.484254, 76.749293, 27.636671],
    '709': [41.770832, 10.912963, 43.417502, 12.713697],
    '710': [27.011578, -30.677607, 29.456145, -28.570573],
    '712': [-61.488727, 15.204544, -61.249528, 15.637912],
    '713': [124.928877, 43.621176, 125.601756, 44.106194],
    '714': [67.342, 36.672028, 75.153963, 41.043827],
    '715': [9.47218, 47.048722, 9.635468, 47.270534],
    '716': [112.361713, 37.622855, 112.731559, 38.100537],
    '717': [120.383627, 27.64514, 120.924602, 28.145309],
    '718': [83.133605, 26.647378, 83.533407, 26.866935],
    '719': [29.000946, -4.468985, 30.84906, -2.309739],
    '720': [106.377522, 26.326774, 106.87201, 26.790916],
    '721': [114.169995, 37.835014, 114.798001, 38.286618],
    '722': [1.413763, 42.428844, 1.786557, 42.65577],
    '723': [115.262347, 38.628233, 115.698992, 39.006051],
    '724': [79.882075, 12.656271, 80.331245, 13.293387],
    '725': [126.279529, 45.440827, 126.99489, 45.866665],
    '726': [74.583199, 26.352072, 74.757911, 26.554536],
    '727': [73.93588, 17.624709, 74.077496, 17.740051],
    '728': [114.110073, 22.929672, 114.602384, 23.263663],
    '729': [118.213176, 24.511235, 119.025465, 25.341741],
    '730': [74.66287, 19.018994, 74.806314, 19.209086],
    '731': [-73.061634, 59.767048, -12.142152, 83.633392],
    '732': [105.981166, 38.330949, 106.429851, 38.712621],
    '733': [115.601145, 28.382987, 116.193798, 28.918502],
    '734': [40.006601, 41.05495, 46.736101, 43.586638],
    '735': [108.12276, 22.679384, 108.555892, 23.01314],
    '737': [120.579711, 31.835998, 121.247063, 32.2063],
    '738': [14.415101, 2.223044, 27.458313, 11.01796],
    '739': [75.80668, 17.534103, 76.005488, 17.740051],
    '740': [12.403564, 43.894031, 12.515976, 43.991951],
    '741': [124.04189, -9.50376, 127.303208, -8.134583],
    '742': [88.745572, 26.702013, 92.125213, 28.246996],
    '743': [120.465146, 30.522893, 121.062563, 31.00271],
    '744': [5.61768, -1.464179, 11.333283, 3.788186],
    '745': [115.919873, 23.104978, 117.286207, 23.823815],
    '747': [120.966131, 37.216848, 121.702067, 37.712883],
    '748': [110.074173, 19.82126, 110.493628, 20.091246],
    '749': [155.512908, -11.845441, 167.19545, -5.434736],
    '751': [-61.805897, 11.984778, -61.422982, 12.527861],
    '752': [103.546229, 35.916047, 104.022045, 36.249255],
    '753': [121.131686, 28.28959, 121.661232, 28.799409],
    '754': [166.522101, -20.251257, 170.238528, -13.071238],
    '755': [116.818213, 34.036151, 117.539409, 34.457831],
    '756': [-62.35304, 16.932855, -61.672168, 17.730422],
    '759': [77.100632, 20.053126, 77.161737, 20.144252],
    '761': [77.988125, 15.700854, 78.104654, 15.889629],
    '762': [112.245646, 34.475237, 112.685569, 34.871595],
    '763': [117.995648, 34.780064, 118.606312, 35.303618],
    '765': [-175.677089, -21.455479, -173.903855, -15.560471],
    '766': [160.953708, 4.571205, 171.929983, 14.616225],
    '767': [117.826989, 39.362336, 118.574923, 39.808653],
    '769': [-172.834649, -14.052084, -171.444644, -13.438789],
    '771': [35.103674, 33.055019, 36.623735, 34.692088],
    '772': [-16.715906, 10.921181, -13.626519, 12.686858],
    '774': [43.228687, -12.390858, 44.540317, -11.365247],
    '775': [118.915935, 36.530265, 119.510779, 36.917283],
    '777': [114.652337, 25.745481, 115.114, 25.972813],
    '778': [166.905285, -0.546271, 166.955355, -0.493785],
    '780': [131.119686, 2.998837, 134.721239, 8.094092],
    '781': [36.433352, 12.354712, 43.136832, 18.021205],
    '784': [6.460004, 0.020572, 7.463376, 1.695155],
    '786': [-61.347563, -52.360423, -57.710854, -51.142347],
    '787': [138.060349, 5.263577, 163.035826, 9.611268],
    '790': [81.742008, 16.908563, 81.898713, 17.0731],
    '794': [-172.227135, -11.445416, 176.845361, 4.725981],
    '795': [-62.867938, 17.092025, -62.542034, 17.415382],
    '796': [124.204596, 37.677081, 130.699062, 43.011478],
    '797': [176.134527, -9.428755, 179.911235, -5.662928],
    '798': [50.750037, 24.471113, 51.647089, 26.184031],
    '799': [-180.0, -90.0, 180.0, -60.512772],
    '800': [-71.366165, 41.578583, -71.303352, 41.663727],
    '801': [-17.104644, 20.769987, -8.667611, 27.667267],
    '809': [-100.764861, 20.285575, -99.656835, 21.664781],
    '810': [7.2895, 44.8437, 8.0473, 45.2963],
    '813': [-97.151661, 19.337845, -96.542989, 19.78737],
    '814': [-5.1566, 41.3552, -4.4978, 41.8263],
    '816': [8.298299, 47.1519, 8.9156, 47.6339],
    '817': [-102.159366, 21.177025, -101.578749, 21.889486],
    '818': [134.745089, 34.083327, 136.107006, 35.377322],
    '819': [-102.570714, 21.622266, -102.064508, 22.075343],
    '820': [23.3911, 46.6534, 23.8555, 46.9344],
    '821': [-97.034077, 16.722567, -96.140455, 17.450399],
    '823': [22.6494, 40.2885, 23.4948, 41.0499],
    '829': [-1.9346, 42.5026, -1.3781, 43.2932],
    '833': [-103.977608, 24.793218, -102.956807, 25.887997],
    '835': [-107.809107, 24.03736, -106.871157, 25.275981],
    '836': [-122.840431, 46.728084, -121.724709, 47.467817],
    '837': [-101.503515, 21.672494, -100.445839, 23.026148],
    '839': [-5.8628, 43.4069, -5.3111, 43.5988],
    '841': [-105.153367, 21.301953, -104.597717, 21.854607],
    '842': [4.12884, 51.9213, 4.5572, 52.1871],
    '845': [-72.314599, 42.008065, -71.478028, 42.721557],
    '849': [136.276575, 34.699603, 137.581098, 35.810603],
    '851': [-98.343485, 22.064403, -97.783043, 22.804452],
    '852': [34.963495, 31.724157, 35.229906, 31.835998],
    '854': [5.7779, 52.3008, 6.6097, 52.6921],
    '856': [5.736719, 49.451728, 6.516826, 50.178593],
    '858': [43.604901, 56.135292, 44.216242, 56.457803],
    '860': [-88.07914, 15.356305, -87.845599, 15.750093],
    '861': [-3.9243, 36.9588, -3.2499, 37.3997],
    '862': [-105.572474, 23.397503, -104.101999, 24.424651],
    '863': [-115.882242, 28.583462, -112.763722, 32.718654],
    '864': [-106.621907, 28.095926, -105.702809, 29.784862],
    '867': [-98.585466, 25.381327, -97.850039, 26.227058],
    '868': [-101.620979, 24.542684, -100.727495, 25.522751],
    '869': [124.493956, 8.365969, 124.860769, 8.617748],
    '870': [-100.002152, 16.687382, -99.473636, 17.230934],
    '871': [-79.125891, -8.227924, -78.925237, -8.008711],
    '872': [29.844828, 59.543094, 31.041563, 60.317098],
    '874': [-8.7595, 42.3199, -8.334899, 42.5945],
    '875': [-70.847394, 19.324835, -70.536381, 19.556428],
    '877': [38.853012, 51.493839, 39.562177, 51.948798],
    '878': [-77.107455, 17.888918, -76.621657, 18.15222],
    '879': [-66.390219, 18.251195, -65.740438, 18.480176],
    '881': [23.674654, 49.709342, 24.28143, 50.024143],
    '882': [-8.113012, 31.52641, -7.859706, 31.784373],
    '883': [151.396164, -33.177093, 152.043696, -32.57885],
    '887': [43.447553, 38.840238, 46.634214, 41.300596],
    '888': [5.5311, 51.7176, 6.0634, 51.9019],
    '890': [28.631586, 39.995149, 29.346378, 40.358221],
    '891': [18.9989, 51.5935, 20.0457, 52.0178],
    '896': [30.371865, 46.172906, 31.02239, 46.759315],
    '897': [30.863378, 30.377412, 31.788649, 31.457665],
    '898': [-44.311648, -22.662704, -43.939437, -22.379268],
    '900': [106.177374, 29.233499, 106.816039, 29.873138],
    '901': [34.815883, 48.18406, 35.299666, 48.589756],
    '903': [30.205627, -29.745087, 30.480161, -29.531807],
    '904': [55.827297, 57.778505, 56.464347, 58.198665],
    '905': [-87.323351, 20.728448, -86.73932, 21.231771],
    '908': [8.6444, 48.5042, 9.64515, 49.0637],
    '909': [23.729121, 58.9917, 25.9548, 59.676203],
    '911': [72.830662, 31.277301, 73.558153, 31.646759],
    '912': [-9.705315, 30.240563, -9.358754, 30.548574],
    '913': [-68.380191, -16.653637, -67.995501, -16.374191],
    '916': [-87.378375, 13.882061, -86.998326, 14.25027],
    '917': [-1.1035, 53.571227, 0.152472, 54.1766],
    '920': [110.182947, -8.049303, 111.103354, -7.262102],
    '921': [-80.88218, 35.750947, -80.027269, 36.55063],
    '922': [128.350372, 35.612079, 128.763372, 36.016297],
    '923': [116.850426, 38.850388, 117.609384, 39.478275],
    '925': [2.9919, 51.06145, 3.40875, 51.348719],
    '927': [-40.566293, -20.741218, -40.435389, -20.591894],
    '928': [86.090677, 23.648147, 86.657555, 23.899128],
    '929': [127.248012, 36.184078, 127.555147, 36.500022],
    '930': [76.120631, 10.406523, 76.362634, 10.66702],
    '931': [105.311672, 28.731379, 105.593356, 29.071698],
    '932': [-1.2236, 53.8746, -0.9232, 54.0566],
    '933': [72.821634, 33.415931, 73.293464, 33.771375],
    '934': [104.478076, 28.637865, 104.757096, 28.931265],
    '935': [76.728715, 17.245925, 76.889739, 17.410577],
    '936': [125.456374, 6.961971, 125.736559, 7.270214],
    '937': [74.771141, 12.550146, 75.089998, 13.105454],
    '938': [117.61855, 36.345809, 118.423648, 37.022961],
    '940': [-75.703883, 10.01771, -75.170984, 10.703666],
    '941': [-1.710544, 12.199232, -1.397369, 12.525657],
    '942': [-2.4708, 52.8791, -1.7816, 53.221],
    '943': [22.1196, 50.8384, 23.3009, 51.5833],
    '944': [-72.677279, 7.64337, -72.346454, 8.430664],
    '945': [77.396918, 9.942719, 77.550404, 10.064748],
    '947': [3.34325, 50.8402, 3.9865, 51.2739],
    '948': [77.695964, 20.791006, 77.823098, 21.006823],
    '949': [66.89086, 30.043969, 67.071154, 30.274768],
    '950': [130.040749, 33.265833, 130.668686, 33.890058],
    '953': [-86.917991, 31.440072, -85.410042, 32.769225],
    '956': [71.307476, 33.762699, 72.314801, 34.327329],
    '957': [-4.251796, 5.220414, -3.791632, 5.52717],
    '958': [35.814021, 49.680761, 36.672464, 50.148364],
    '959': [48.566186, 55.603288, 49.422567, 55.994503],
    '960': [112.072401, 30.197813, 112.396681, 30.497214],
    '961': [0.6287, 51.930384, 1.6655, 52.3688],
    '962': [45.899686, 37.870397, 46.579544, 38.180264],
    '965': [1.7862, 49.6343, 2.7697, 50.2386],
    '967': [5.5643, 46.9809, 6.4963, 47.5514],
    '968': [-4.797569, 48.2817, -4.0461, 48.6764],
    '969': [-0.8932, 48.8202, 0.1174, 49.3461],
    '970': [18.322193, -34.196904, 18.880188, -33.684631],
    '971': [5.623, 45.3866, 6.2831, 45.7808],
    '972': [38.829208, -7.002525, 39.377491, -6.597046],
    '973': [5.3113, 44.7328, 6.1948, 45.5061],
    '974': [78.169975, 17.18008, 78.741137, 17.682376],
    '975': [27.573307, -26.503908, 28.506232, -25.77915],
    '976': [7.9617, 48.74935, 8.6875, 49.20995],
    '977': [9.0943, 51.0806, 9.8603, 51.6266],
    '978': [-1.3595, 45.9552, -0.6863, 46.4386],
    '979': [-0.2309, 47.7018, 0.6142, 48.2966],
    '980': [0.8074, 45.51, 1.7542, 46.1818],
    '981': [6.8972, 47.5801, 7.5738, 47.9768],
    '983': [10.5117, 49.1248, 11.6265, 49.7389],
    '984': [1.3378, 47.5625, 2.4654, 48.1464],
    '985': [-0.6638, 43.0232, -0.0263, 43.5759],
    '986': [2.3191, 42.41144, 3.0641, 42.9183],
    '987': [-0.0673, 46.2567, 0.7658, 46.8118],
    '988': [10.4145, 44.4834, 10.7732, 44.8627],
    '989': [0.6321, 49.1845, 1.5896, 49.7959],
    '990': [111.30335, 27.137381, 111.656112, 27.399613],
  },
  [LocationType.STATE]: {
    '1513': [64.494153, 29.539127, 67.790234, 32.480367],
    '1482': [68.825984, 34.243196, 69.936031, 34.891348],
    '2203': [69.520878, 33.97564, 71.125456, 34.797018],
    '314': [66.447824, 35.67826, 68.173139, 37.357635],
    '1277': [60.517603, 32.797392, 64.449383, 35.589936],
    '2345': [-8.151424, 54.036472, -5.438172, 55.251032],
    '984': [-5.710046, 49.983332, 1.754926, 55.810996],
    '3027': [-7.435039, 54.707618, -0.992471, 60.657769],
    '3667': [-5.292477, 51.382326, -2.666091, 53.413844],
    '406': [19.737453, 40.3774, 20.465732, 40.874251],
    '923': [19.417833, 41.273064, 19.904853, 41.582233],
    '3418': [19.446027, 40.998942, 20.223853, 41.506218],
    '3405': [0.486753, 34.073319, 2.670846, 35.711357],
    '2467': [2.996532, 28.498373, 9.47078, 33.958668],
    '774': [6.309767, 36.092683, 7.049118, 36.624204],
    '3125': [6.239375, 36.419475, 7.405151, 37.089679],
    '3417': [1.642358, 36.355865, 2.832811, 36.698695],
    '3425': [-2.218498, 34.096781, -0.767631, 35.238175],
    '3093': [-1.125253, 33.908456, 0.040749, 35.456157],
    '3050': [4.729645, 35.615122, 6.026243, 36.595602],
    '1193': [6.939499, 36.024409, 7.959225, 36.672902],
    '367': [4.741007, 34.753621, 6.792764, 35.922747],
    '121': [2.799867, 36.579427, 3.382583, 36.819708],
    '1736': [1.335249, 32.804036, 4.479801, 34.695157],
    '2132': [-0.115647, 35.672047, 0.740988, 36.33394],
    '450': [2.476479, 36.32975, 3.307116, 36.672591],
    '387': [4.350452, 36.217605, 5.482679, 36.895941],
    '174': [7.287415, 36.602898, 7.830145, 37.083939],
    '3423': [3.715829, 36.452644, 4.652958, 36.910499],
    '2078': [-91.654033, 30.180922, -88.097893, 34.996024],
    '2301': [-84.321873, 33.843238, -75.461889, 36.588124],
    '2415': [-103.002527, 33.615839, -94.430669, 37.002212],
    '3635': [-83.675399, 36.540745, -75.242268, 39.46602],
    '3701': [-82.639058, 37.20149, -77.719522, 40.638809],
    '1846': [-94.043154, 28.931645, -88.817022, 33.019463],
    '2047': [-90.418143, 41.696126, -82.416818, 48.190984],
    '1990': [-73.508143, 41.238319, -69.930148, 42.884598],
    '1333': [-117.242688, 41.988558, -111.043576, 49.00075],
    '1043': [-87.634943, 24.545808, -80.032199, 31.00089],
    '2221': [-104.053524, 40.000005, -95.308298, 43.001385],
    '3675': [-124.731843, 45.543547, -116.916003, 49.002232],
    '2236': [-109.050183, 31.33237, -103.002208, 37.000238],
    '3426': [-67.94469, 17.930069, -65.244112, 18.512917],
    '3180': [-104.05789, 42.480043, -96.439343, 45.945317],
    '3387': [-106.645306, 25.840121, -93.518919, 36.500532],
    '572': [-124.408615, 32.534301, -114.131222, 42.00949],
    '108': [-88.473232, 30.223651, -84.889084, 35.008035],
    '1097': [-85.60517, 30.358935, -80.842013, 35.000666],
    '2533': [-80.519429, 39.719808, -74.691131, 42.269868],
    '2079': [-95.773557, 35.99569, -89.0996, 40.613647],
    '764': [-109.060073, 36.992432, -102.041583, 41.00345],
    '3538': [-114.052974, 36.998003, -109.041069, 42.001708],
    '3381': [-90.309883, 34.982979, -81.646904, 36.678125],
    '3732': [-111.054568, 40.994752, -104.05235, 45.005822],
    '2240': [-79.761954, 40.49745, -71.856216, 45.015825],
    '1520': [-102.051753, 36.993022, -94.58842, 40.003085],
    '114': [-179.146858, 51.220509, 179.773026, 71.351756],
    '2230': [-120.005759, 35.001862, -114.03966, 42.001828],
    '1362': [-91.512981, 36.970305, -87.49591, 42.508489],
    '3604': [-73.437741, 42.726862, -71.496018, 45.016617],
    '2109': [-116.049169, 44.357968, -104.039705, 49.001008],
    '1386': [-96.639712, 40.378271, -90.140067, 43.500558],
    '3179': [-83.353242, 32.035366, -78.543306, 35.215402],
    '2233': [-72.557248, 42.696999, -70.711955, 45.305414],
    '216': [-114.816311, 31.33234, -109.045234, 37.004266],
    '3676': [-77.119761, 38.791653, -76.909397, 38.995118],
    '950': [-170.847018, -14.369054, -169.446304, -14.157808],
    '2885': [-65.042084, 17.680428, -64.577206, 18.371272],
    '2235': [-75.559448, 38.928527, -73.89648, 41.357431],
    '1982': [-79.487654, 37.923172, -75.048941, 39.722783],
    '1904': [-71.083919, 43.064727, -66.976495, 47.459696],
    '1266': [-160.247502, 18.912447, -154.807044, 22.229515],
    '845': [-75.78866, 38.451021, -75.048941, 39.839015],
    '1224': [144.619294, 13.246271, 144.955416, 13.652207],
    '2891': [145.12706, 14.112385, 145.851159, 19.683722],
    '2744': [-71.860319, 41.306326, -71.120571, 42.018807],
    '1563': [-89.571247, 36.497136, -81.968016, 39.146666],
    '2409': [-84.820162, 38.405145, -80.518697, 41.977531],
    '3725': [-92.887937, 42.491991, -86.824389, 47.077183],
    '2440': [-124.559312, 41.9918, -116.463517, 46.267086],
    '2304': [-104.048911, 45.935145, -96.554516, 49.000542],
    '217': [-94.617926, 33.004112, -89.644844, 36.499607],
    '1379': [-88.094962, 37.77323, -84.786411, 41.7606],
    '2065': [-97.238397, 43.499364, -89.581014, 49.384366],
    '772': [-73.727776, 40.987577, -71.786995, 42.050596],
    '2871': [-61.815443, 16.99922, -61.733077, 17.065031],
    '533': [-63.406054, -41.031918, -56.668368, -30.151774],
    '620': [-69.10016, -30.116125, -64.844891, -25.131926],
    '681': [-63.271647, -28.010021, -58.370688, -24.164428],
    '731': [-72.142858, -46.003645, -63.600062, -41.969827],
    '534': [-58.745046, -34.787345, -58.339474, -34.410133],
    '780': [-65.702025, -35.004464, -61.80638, -29.427533],
    '785': [-59.615745, -30.591262, -55.616075, -27.301516],
    '1046': [-62.313677, -26.871786, -57.573159, -22.512505],
    '1470': [-67.205299, -24.589121, -64.079873, -21.843458],
    '1713': [-68.165894, -39.364242, -63.341008, -34.996528],
    '1717': [-69.634093, -31.921212, -65.328516, -27.735848],
    '2032': [-70.58369, -37.527319, -66.45481, -31.959947],
    '2074': [-55.965575, -28.125161, -53.637481, -25.554612],
    '2229': [-71.942268, -41.059493, -68.164802, -36.171032],
    '2758': [-71.906264, -42.00249, -62.800063, -37.540446],
    '2906': [-68.561465, -26.301955, -62.275209, -22.000757],
    '2937': [-67.321129, -36.000164, -64.83391, -31.826867],
    '2971': [-73.531182, -52.366255, -65.804057, -45.969347],
    '2975': [-62.872476, -34.384612, -58.807222, -28.009439],
    '2985': [-65.022518, -30.120678, -61.627238, -25.579396],
    '3410': [-68.5499, -54.844037, -65.775049, -53.052738],
    '3488': [-66.055989, -27.936902, -64.368848, -26.083255],
    '161': [1.461063, 42.464222, 1.562297, 42.520724],
    '400': [12.528155, -13.875305, 15.116545, -11.763143],
    '558': [12.013806, -5.772872, 13.102718, -4.388063],
    '1312': [14.79939, -13.767398, 16.588112, -11.434677],
    '1859': [12.992361, -10.440388, 14.629871, -8.744105],
    '3784': [12.273424, -7.801146, 14.985383, -5.826602],
    '3302': [45.685887, 38.85015, 46.574801, 39.875678],
    '3075': [43.447139, 40.419984, 44.186899, 41.172004],
    '1837': [43.967477, 40.663398, 44.925646, 41.235303],
    '1646': [44.408525, 40.090149, 45.006564, 40.700351],
    '3764': [44.415615, 40.060156, 44.640495, 40.229344],
    '1171': [6.175235, 52.838197, 7.226742, 53.547299],
    '913': [6.11982, 52.612196, 7.0927, 53.203822],
    '3541': [4.792043, 51.857361, 5.627309, 52.303619],
    '1038': [5.119083, 52.249528, 6.017299, 52.843983],
    '2476': [5.777931, 52.118105, 7.072283, 52.854215],
    '2309': [4.492445, 52.165929, 5.335462, 53.18375],
    '1093': [4.99393, 51.733581, 6.83269, 52.522025],
    '3185': [3.835463, 51.655361, 5.031414, 52.328603],
    '1050': [4.850833, 52.800719, 6.427615, 53.510418],
    '2299': [4.192417, 51.221057, 6.048119, 51.830731],
    '1800': [5.566857, 50.750822, 6.22681, 51.778627],
    '3803': [3.358973, 51.200183, 4.277425, 51.760418],
    '2238': [140.999272, -37.505147, 159.10543, -28.156879],
    '3613': [140.961866, -39.13442, 149.976198, -33.980636],
    '2685': [137.994654, -29.179223, 153.552177, -10.061306],
    '3177': [129.001847, -38.060046, 141.002963, -25.996378],
    '3708': [112.921125, -35.134676, 129.001928, -13.740768],
    '3362': [143.837082, -43.64318, 148.478729, -39.450589],
    '2356': [129.000448, -25.998599, 137.999061, -10.975417],
    '261': [148.762796, -35.920517, 149.399293, -35.124403],
    '544': [15.997287, 46.831093, 17.247439, 48.11859],
    '604': [12.657454, 46.372659, 15.066024, 47.131776],
    '1850': [14.453568, 47.422602, 17.06909, 49.020677],
    '3525': [12.749977, 47.461613, 14.993184, 48.772712],
    '2909': [12.07665, 46.944295, 13.996928, 48.042057],
    '3263': [13.564109, 46.612342, 16.172887, 47.828385],
    '3505': [10.098089, 46.651808, 12.96664, 47.74299],
    '3649': [9.530384, 46.841445, 10.237091, 47.596126],
    '3617': [16.183006, 48.118392, 16.578742, 48.323179],
    '1079': [44.971461, 38.393366, 50.621812, 41.91234],
    '2237': [-77.554581, 24.976526, -77.255521, 25.081249],
    '1048': [-78.999863, 26.490417, -78.401253, 26.798721],
    '2867': [-59.636059, 13.076746, -59.567417, 13.157641],
    '1131': [27.281538, 51.276415, 31.79927, 53.327567],
    '3639': [26.209464, 54.234828, 31.200017, 56.171169],
    '1170': [23.511435, 52.745525, 26.685554, 55.004348],
    '515': [23.199211, 51.50321, 27.62793, 53.402978],
    '2087': [28.119801, 52.684761, 32.73602, 54.480307],
    '2066': [26.013876, 52.391328, 29.489411, 55.009009],
    '2067': [27.475099, 53.823195, 27.732982, 53.958272],
    '524': [4.283083, 50.793103, 4.485079, 50.898953],
    '1037': [2.546011, 50.708343, 5.788199, 51.466731],
    '3668': [3.176996, 49.617111, 6.403956, 50.811924],
    '393': [-88.648328, 17.100709, -87.786078, 18.185175],
    '625': [-89.193769, 16.403519, -88.542187, 17.488389],
    '3400': [89.405918, 27.117879, 89.893139, 27.978014],
    '967': [-67.561967, -16.491651, -61.533454, -10.381218],
    '737': [-65.695133, -21.517323, -62.179371, -18.347264],
    '755': [-67.005561, -18.681062, -64.234106, -15.779917],
    '1714': [-69.990362, -18.042856, -66.732113, -11.849681],
    '2450': [-69.142341, -19.88671, -66.086104, -17.324537],
    '3351': [-65.417152, -22.872849, -62.260915, -20.885813],
    '3226': [16.194912, 42.55719, 19.619963, 45.24381],
    '1029': [15.729853, 42.606673, 19.03487, 45.230579],
    '1063': [25.539009, -25.467295, 26.188595, -24.512628],
    '630': [23.884131, -23.962316, 29.37501, -19.000034],
    '2329': [20.971986, -22.000342, 25.178186, -18.000786],
    '3606': [7.096287, 58.770257, 10.529577, 60.188272],
    '41': [6.384083, 57.979804, 9.366075, 59.672687],
    '3623': [7.438842, 58.877216, 11.945505, 61.09172],
    '2777': [5.166, 58.278024, 7.214667, 59.844574],
    '2288': [11.745747, 64.939497, 18.151355, 69.324719],
    '2124': [5.340323, 61.92344, 9.364831, 63.477705],
    '3607': [4.641404, 59.475421, 8.322053, 62.215416],
    '2457': [10.489165, 59.809403, 10.951389, 60.135106],
    '3477': [15.592542, 68.357219, 31.132669, 71.183746],
    '1383': [7.34253, 59.840785, 12.870847, 62.696928],
    '571': [-75.922621, 4.802514, -74.623351, 5.778625],
    '807': [-74.890666, 3.725614, -73.050834, 5.836703],
    '184': [-77.12689, 5.422553, -73.879592, 8.889743],
    '246': [-75.267143, 10.253183, -74.713238, 11.082382],
    '1898': [-74.945591, 8.917108, -73.54255, 11.348525],
    '3572': [-77.53408, 3.091089, -75.704871, 5.036871],
    '2979': [-74.526236, 5.707211, -72.476466, 8.143419],
    '2210': [-79.005301, 0.361537, -76.834197, 2.630236],
    '462': [-74.451665, 3.728441, -73.997469, 4.8369],
    '622': [-77.925729, 0.958752, -75.747505, 3.325884],
    '2925': [-81.735537, 12.483111, -81.349561, 13.385847],
    '1163': [43.228807, -11.938109, 43.517672, -11.36614],
    '138': [-73.791556, -9.766762, -56.128946, 2.232406],
    '2499': [-58.896745, -9.846804, -46.044113, 2.528525],
    '1959': [-48.515146, -10.230195, -41.824285, -1.181627],
    '626': [-41.431634, -7.882166, -37.237861, -2.874453],
    '2756': [-38.523065, -6.987008, -34.9667, -4.88751],
    '2502': [-38.704477, -8.331487, -34.795062, -6.062665],
    '2563': [-46.014093, -10.925773, -40.379619, -2.86023],
    '2541': [-41.332527, -9.508008, -34.810061, -7.297107],
    '109': [-38.174155, -10.403205, -35.138804, -8.815943],
    '3047': [-38.179152, -11.552939, -36.337535, -9.533109],
    '3429': [-50.746543, -13.440121, -45.670512, -5.188969],
    '294': [-46.602619, -18.310602, -37.415914, -8.57018],
    '1997': [-61.592028, -18.076162, -50.241802, -7.315994],
    '1128': [-53.24565, -19.521027, -45.908423, -12.454364],
    '1028': [-48.281711, -16.044708, -47.321718, -15.534016],
    '2063': [-51.052003, -22.951725, -39.835846, -14.274772],
    '1002': [-41.900903, -21.293877, -39.667506, -17.911412],
    '1998': [-58.130332, -24.077128, -50.934661, -17.181613],
    '2761': [-44.924486, -23.390071, -40.966863, -20.812675],
    '2997': [-53.129265, -25.326459, -44.243446, -19.80951],
    '2504': [-54.619305, -26.72816, -48.127981, -22.55707],
    '2968': [-53.798477, -29.377924, -48.421609, -26.046186],
    '2757': [-57.614616, -33.732251, -49.765442, -27.110411],
    '1354': [1.4463, 48.120077, 3.559027, 49.241488],
    '663': [0.052739, 46.346901, 3.128678, 48.941034],
    '498': [2.844895, 46.156065, 7.143363, 48.400053],
    '2290': [-1.94763, 48.17989, 1.802863, 50.072204],
    '1264': [1.380218, 48.837526, 4.573052, 51.088665],
    '1158': [3.383694, 47.420833, 8.232633, 50.169104],
    '2516': [-2.558523, 46.266836, 0.916651, 48.567994],
    '521': [-5.140664, 47.279583, -1.015613, 48.881274],
    '2364': [-1.789639, 42.778171, 2.611285, 47.175991],
    '2215': [-0.327176, 42.332499, 4.845569, 45.046654],
    '263': [2.062878, 44.115493, 7.185081, 46.804232],
    '2638': [4.230271, 43.028721, 7.716943, 45.126844],
    '786': [8.5399, 41.364639, 9.559556, 43.011709],
    '1257': [25.48687, -13.459035, 29.814759, -7.58031],
    '2284': [27.20624, -2.06577, 29.987254, 0.96417],
    '1858': [21.751542, -11.708294, 27.15004, -7.657586],
    '1605': [15.128221, -5.032536, 16.534075, -3.927611],
    '511': [14.953747, -4.512163, 15.962573, -3.428232],
    '2357': [172.643784, -36.396343, 174.746734, -34.396278],
    '258': [174.162035, -37.291832, 175.550624, -36.039292],
    '3663': [174.613411, -39.300326, 176.505325, -36.469582],
    '376': [175.805921, -39.005085, 178.107555, -37.267631],
    '1114': [177.122171, -38.972812, 178.550456, -37.535416],
    '1268': [176.042947, -40.431291, 178.001674, -38.583921],
    '3348': [173.752884, -39.86877, 174.991765, -38.709988],
    '1931': [174.675165, -40.779585, 176.632958, -38.470327],
    '3679': [174.613267, -41.613549, 176.33276, -40.65921],
    '3686': [168.055885, -44.499061, 172.692098, -40.777387],
    '592': [169.542498, -45.080929, 174.063259, -41.907383],
    '2461': [168.116393, -46.637971, 171.140684, -43.955425],
    '3220': [166.426959, -47.289993, 169.279166, -44.255121],
    '3361': [172.050587, -42.305571, 173.304367, -40.498756],
    '2226': [173.199673, -41.393422, 173.598762, -41.052288],
    '1971': [172.71853, -42.488129, 174.391499, -40.690915],
    '391': [114.080576, 4.004995, 114.854116, 4.708673],
    '523': [114.778614, 4.730446, 115.063465, 5.045395],
    '543': [26.563873, 41.926787, 28.031883, 42.965985],
    '1064': [24.850494, 42.739223, 25.630268, 43.210434],
    '2108': [22.737974, 43.114993, 23.633201, 43.85154],
    '2581': [23.952025, 43.221829, 25.229183, 43.764852],
    '2583': [24.342745, 41.697717, 25.327771, 42.798381],
    '2795': [25.454077, 43.318788, 26.532128, 44.018869],
    '3147': [22.744009, 42.096082, 24.478262, 43.206376],
    '3149': [23.031118, 42.417054, 23.639038, 42.892409],
    '3248': [24.97548, 42.024124, 26.173649, 42.791134],
    '3350': [25.936188, 42.949015, 26.763876, 43.559579],
    '3583': [27.081593, 42.828017, 28.050883, 43.597058],
    '3598': [25.068243, 42.762791, 26.171846, 43.6485],
    '667': [-2.928885, 10.977, -1.309292, 12.841254],
    '1263': [-5.4884, 10.683765, -2.82997, 12.107663],
    '535': [29.310733, -3.442721, 29.424526, -3.305715],
    '3000': [-25.085967, 16.777006, -24.859459, 16.922588],
    '2619': [-23.586007, 14.901896, -23.456986, 14.996829],
    '2555': [104.712665, 11.422662, 105.040851, 11.726061],
    '368': [102.345081, 12.371297, 103.874666, 13.479363],
    '662': [10.259376, 3.141918, 13.23284, 6.263505],
    '1021': [13.398794, 9.933482, 15.689687, 13.079035],
    '2293': [12.243795, 7.020774, 15.591449, 10.219158],
    '2330': [9.586607, 5.740231, 11.19655, 7.076053],
    '22': [11.19454, 5.989661, 15.243063, 8.183361],
    '928': [12.520498, 1.790505, 16.186846, 6.086645],
    '3172': [9.821197, 2.172199, 13.674738, 3.66753],
    '3201': [8.51508, 3.917622, 10.100865, 6.529626],
    '3680': [9.870502, 4.897762, 11.299437, 6.298261],
    '1812': [9.335349, 3.274991, 11.123676, 5.355909],
    '2241': [-67.821685, 46.611429, -52.621807, 60.377744],
    '2632': [-64.413575, 45.948294, -61.971417, 47.05625],
    '2366': [-66.335445, 43.407619, -59.710672, 47.041968],
    '2232': [-69.052994, 44.598721, -63.773089, 48.071784],
    '2683': [-79.762663, 44.991325, -57.105486, 62.581536],
    '2425': [-97.176286, 41.68132, -74.343472, 56.854617],
    '1946': [-102.007609, 48.998878, -88.990287, 60.000062],
    '3007': [-110.010393, 48.998922, -101.361902, 60.000062],
    '116': [-120.002698, 48.99674, -109.999641, 60.000062],
    '520': [-139.052201, 48.309471, -114.053758, 60.000062],
    '3775': [-141.002617, 60.0, -123.789325, 69.646315],
    '2359': [-136.46869, 60.0, -102.0, 78.765388],
    '2381': [-120.724541, 51.255481, -61.101696, 83.137086],
    '1279': [-84.199756, 9.959312, -83.710685, 10.791618],
    '1179': [-85.93482, 9.73791, -84.766999, 11.219568],
    '110': [-85.43202, 9.827839, -84.155593, 11.084402],
    '2929': [-84.59283, 9.07996, -83.42514, 10.187253],
    '2653': [-87.094251, 5.49961, -82.712005, 10.33608],
    '607': [-84.088116, 9.500466, -83.310743, 10.146738],
    '1801': [-83.947174, 9.06969, -82.552657, 10.93786],
    '7': [-4.461135, 5.219217, -3.716347, 5.605083],
    '3751': [-5.52989, 6.551195, -4.954416, 7.079446],
    '1127': [-6.412119, 5.159599, -4.936361, 6.650208],
    '3008': [-7.085183, 6.260994, -5.39974, 7.803232],
    '3015': [-6.967831, 8.538627, -3.7977, 10.74001],
    '3573': [-5.77161, 7.231243, -3.899953, 9.422105],
    '768': [-3.850877, 5.092758, -2.724146, 7.354412],
    '2456': [17.884142, 45.209935, 19.098714, 45.918032],
    '3129': [17.076453, 45.043769, 18.572372, 45.392892],
    '3120': [15.767552, 44.995689, 17.192859, 45.670401],
    '444': [16.489034, 45.470616, 17.496734, 46.090517],
    '2019': [16.240857, 46.290703, 16.853066, 46.554847],
    '1637': [16.3417, 45.884183, 17.30569, 46.357308],
    '3580': [15.871505, 46.009738, 16.761904, 46.405202],
    '749': [15.773605, 45.614205, 16.226938, 45.969016],
    '3783': [15.322752, 45.465368, 16.710859, 46.069505],
    '1407': [13.490408, 44.766083, 14.228555, 45.519953],
    '2631': [14.107188, 44.473888, 15.22775, 45.672944],
    '3089': [15.220485, 43.487048, 16.544656, 44.218604],
    '3224': [16.013088, 43.005636, 17.449061, 43.974109],
    '918': [16.627638, 42.392178, 18.533662, 43.177468],
    '1265': [-82.547333, 22.933711, -82.081421, 23.180659],
    '1996': [-82.157841, 22.037889, -80.468699, 23.22282],
    '1299': [-76.727964, 20.384442, -74.716278, 21.247843],
    '3626': [-80.756348, 21.951752, -78.958479, 23.118621],
    '2201': [14.42423, 4.983315, 16.537806, 6.688658],
    '331': [18.480976, 4.324248, 18.660762, 4.49841],
    '2171': [14.886999, 12.054809, 15.182573, 12.253487],
    '185': [-70.737128, -26.061008, -66.9905, -20.934614],
    '215': [-70.377346, -19.229342, -68.912354, -17.4984],
    '242': [-71.525608, -29.535151, -68.263089, -25.28584],
    '270': [-75.625579, -49.211304, -71.091653, -43.638122],
    '777': [-71.717455, -32.282156, -69.80919, -29.037866],
    '205': [-73.518375, -39.637137, -70.827381, -37.581733],
    '1838': [-74.833571, -44.067036, -71.581183, -40.236809],
    '1840': [-73.723978, -40.68098, -71.592746, -39.287737],
    '2372': [-72.884391, -37.197621, -70.95289, -36.005728],
    '3349': [-70.285304, -21.630212, -68.404933, -18.936706],
    '3576': [-109.449861, -33.955888, -69.989235, -26.28735],
    '431': [-73.684209, -38.492463, -70.983319, -36.443268],
    '2389': [-72.056261, -35.005289, -69.86158, -33.850598],
    '2001': [-72.7859, -36.543565, -70.31258, -34.684626],
    '2982': [-71.714492, -34.290669, -69.732306, -32.922479],
    '1227': [108.647903, 18.197916, 110.925115, 20.014584],
    '1182': [104.581048, 21.5466, 112.076879, 26.469079],
    '1054': [115.968416, 23.659459, 120.5414, 28.390088],
    '3776': [97.773324, 21.142471, 106.203855, 29.340099],
    '1198': [103.850541, 24.780522, 109.575437, 29.188134],
    '1455': [113.67845, 24.587543, 118.590948, 29.973697],
    '1320': [109.328846, 24.857425, 114.317558, 30.236499],
    '3808': [118.29055, 27.287414, 121.961252, 31.225061],
    '3064': [121.001869, 30.742094, 121.414548, 31.499584],
    '727': [105.373039, 28.465411, 110.205645, 32.299693],
    '1315': [108.531655, 29.196598, 116.2457, 33.393724],
    '3091': [97.430436, 26.32334, 108.735223, 34.323978],
    '169': [115.341237, 29.512702, 119.739997, 34.732202],
    '1454': [116.456451, 31.092953, 121.907421, 35.143303],
    '1275': [110.439703, 31.518356, 116.269665, 36.462207],
    '3407': [78.393465, 27.732482, 99.121454, 36.504496],
    '3063': [114.94853, 34.545418, 121.561205, 38.276752],
    '2267': [104.542734, 35.420256, 107.581407, 39.131634],
    '3055': [105.825759, 31.972289, 111.271805, 39.50272],
    '3404': [116.904218, 38.706462, 118.119251, 40.300029],
    '3065': [110.439703, 34.719869, 114.622318, 40.791578],
    '385': [115.841867, 39.628526, 117.495487, 41.073365],
    '1080': [92.646187, 32.654252, 108.782564, 42.659925],
    '1271': [113.608871, 36.220232, 119.97127, 42.662849],
    '1786': [118.956272, 39.149533, 125.772696, 43.439835],
    '1459': [122.142106, 40.878303, 131.305765, 46.349393],
    '3739': [73.606083, 34.391716, 96.403962, 49.17895],
    '1382': [96.116579, 37.573521, 125.946667, 53.347183],
    '1272': [121.336806, 43.4577, 134.775456, 53.5556],
    '1652': [114.039613, 22.333801, 114.400238, 22.604047],
    '1181': [109.742125, 21.048753, 117.215446, 25.585526],
    '1757': [33.128659, 34.71859, 33.749833, 35.107738],
    '1798': [32.640667, 34.632303, 33.276794, 34.95975],
    '2480': [32.269213, 34.657627, 32.7973, 35.175408],
    '2259': [32.567831, 34.883603, 33.594669, 35.279822],
    '144': [33.541426, 34.956122, 34.586749, 35.694851],
    '1570': [32.920387, 35.228433, 33.671726, 35.403112],
    '1291': [14.22448, 49.9419, 14.706787, 50.177394],
    '639': [13.396472, 49.501345, 15.535457, 50.61895],
    '1457': [13.53597, 48.551934, 15.604205, 49.621113],
    '2584': [12.401112, 48.942248, 13.840351, 50.104372],
    '1535': [12.091708, 49.890404, 13.301753, 50.459447],
    '3536': [12.942066, 50.07695, 14.65228, 51.055384],
    '1789': [14.342644, 50.4719, 15.632568, 51.023251],
    '1655': [15.104231, 50.038485, 16.585407, 50.780161],
    '2505': [15.362811, 49.57218, 16.867396, 50.207374],
    '3659': [14.888181, 48.937589, 16.418304, 49.861106],
    '3192': [15.542473, 48.616661, 17.646932, 49.632865],
    '2418': [16.711996, 49.268794, 17.917961, 50.449454],
    '2121': [17.146527, 49.392589, 18.859245, 50.327971],
    '3816': [17.109815, 48.854171, 18.415483, 49.539766],
    '646': [8.106746, 55.640546, 11.635315, 56.80736],
    '3802': [10.889058, 54.595614, 12.550621, 55.990446],
    '595': [11.847795, 54.996218, 15.154838, 56.125907],
    '3181': [8.078619, 54.735494, 10.958321, 55.912458],
    '885': [41.761362, 10.971604, 42.706572, 11.874914],
    '2838': [-61.391174, 15.260413, -61.301061, 15.351949],
    '1707': [-68.935296, 18.204629, -68.334055, 18.975808],
    '2647': [-71.221774, 19.517827, -70.372716, 19.932982],
    '2926': [-70.356553, 18.234131, -70.00798, 18.801721],
    '2987': [-70.167184, 18.410312, -69.52811, 18.731673],
    '2175': [-69.997401, 18.426464, -69.879384, 18.493884],
    '2509': [-78.175065, -2.610868, -75.581013, -0.999395],
    '602': [-78.543797, 0.362944, -77.565061, 1.197772],
    '1828': [-80.49374, -4.74047, -79.097749, -3.33784],
    '3790': [-79.429884, -5.01147, -78.370291, -3.343683],
    '969': [-80.24106, -3.893903, -79.36493, -3.048214],
    '1000': [-80.105119, 0.075356, -78.433208, 1.406252],
    '1367': [-79.274873, 0.127986, -77.810011, 0.887042],
    '3269': [-77.971978, -0.638274, -75.224721, 0.653061],
    '2974': [-81.00476, -2.508093, -80.226944, -1.665582],
    '2988': [-79.625876, -0.695302, -78.747347, 0.126536],
    '2564': [-79.36917, -0.710949, -77.843312, 0.325894],
    '1926': [-80.910364, -1.953997, -79.405028, 0.355101],
    '275': [-79.760358, -3.625388, -78.441017, -2.56433],
    '580': [-79.522178, -2.841184, -78.5684, -2.2362],
    '1191': [-80.5613, -3.070858, -79.118176, -0.835416],
    '1839': [-79.873103, -2.131772, -79.092295, -0.540265],
    '790': [-79.334856, -1.214921, -78.392534, -0.338415],
    '470': [-79.358089, -2.192711, -78.845772, -1.152948],
    '3496': [-78.940247, -1.516431, -78.116727, -0.994014],
    '710': [-79.136954, -2.553864, -78.359575, -1.430725],
    '2206': [-78.425342, -1.241175, -77.045684, 0.031636],
    '2442': [-77.614241, -1.549822, -75.192622, -0.036311],
    '2323': [32.573538, 29.534469, 34.878445, 31.323521],
    '3197': [32.63233, 27.728078, 34.903618, 29.92325],
    '240': [31.098146, 21.720887, 34.242863, 25.336946],
    '2728': [31.788114, 22.000002, 35.793446, 29.137155],
    '1999': [24.706667, 27.738004, 30.31119, 31.670392],
    '120': [29.58752, 30.359675, 30.093122, 31.327119],
    '1402': [31.745819, 30.202716, 32.781795, 30.964343],
    '3276': [31.844541, 28.98949, 32.832985, 30.261509],
    '1103': [30.747159, 30.574621, 31.301326, 31.103808],
    '1016': [29.84995, 28.961459, 31.12239, 29.71627],
    '402': [29.779653, 28.269095, 31.97111, 29.436159],
    '2068': [28.625062, 27.618225, 32.653929, 28.802631],
    '241': [30.654167, 26.893073, 32.75362, 27.909764],
    '3150': [31.296296, 26.191813, 32.798686, 27.374278],
    '2667': [32.432127, 25.408389, 32.967948, 25.984598],
    '1117': [27.310595, 27.738004, 31.906282, 30.339705],
    '2115': [30.485854, 30.170943, 31.257586, 30.702229],
    '384': [29.616867, 29.920295, 30.851784, 31.46669],
    '565': [31.220495, 29.748306, 31.909005, 30.320917],
    '2662': [31.056113, 30.093036, 31.630124, 30.574621],
    '828': [31.49087, 31.105311, 31.961675, 31.526012],
    '1486': [30.368107, 30.962854, 31.378559, 31.601594],
    '2605': [32.033578, 30.868452, 32.593976, 31.290769],
    '3067': [31.278957, 30.197966, 32.225544, 31.062244],
    '1710': [-89.631389, 13.425218, -89.147504, 14.06473],
    '2942': [-88.528832, 13.167085, -88.019237, 13.918944],
    '2966': [-89.746084, 13.778295, -89.248729, 14.450557],
    '2946': [-89.292862, 13.475534, -89.025446, 14.064777],
    '432': [8.611242, 3.505508, 8.937924, 3.788098],
    '1810': [9.341228, 1.001007, 10.257458, 2.348202],
    '1894': [38.681284, 15.129975, 39.037644, 15.601332],
    '2479': [23.610478, 57.864544, 25.30669, 58.757014],
    '1332': [26.702331, 58.86564, 28.210017, 59.50053],
    '3355': [26.078548, 58.113041, 27.534272, 58.682793],
    '26': [38.660223, 8.841074, 38.907086, 9.099186],
    '142': [35.257874, 8.715881, 40.213314, 13.769345],
    '1246': [42.062243, 9.190722, 42.271228, 9.373516],
    '2449': [34.145517, 3.505868, 42.978911, 10.38655],
    '2802': [34.87783, 4.444425, 39.128229, 8.458713],
    '3156': [39.094903, 3.455971, 48.001056, 11.096225],
    '3411': [36.451978, 12.265766, 39.985139, 14.830107],
    '631': [177.799676, -18.272783, 178.659065, -17.553564],
    '3702': [177.246246, -18.229878, 178.450687, -17.318777],
    '3547': [22.809846, 59.808079, 26.545381, 60.840852],
    '3221': [21.17301, 59.84775, 23.920541, 61.101432],
    '3196': [26.215481, 61.1513, 29.696816, 62.654145],
    '2320': [26.039828, 62.141032, 29.097695, 64.036745],
    '1521': [23.135147, 60.552499, 25.26907, 61.32485],
    '2459': [24.013026, 61.450703, 26.780323, 63.612289],
    '2568': [22.434164, 60.939727, 24.970526, 62.494328],
    '3193': [21.511595, 61.979945, 24.584299, 63.498047],
    '650': [22.81575, 63.116424, 25.036325, 64.092072],
    '2483': [24.855479, 60.671651, 26.544902, 61.801419],
    '1754': [20.550396, 65.589301, 30.017341, 70.091845],
    '2317': [23.5839, 63.429111, 30.138463, 66.486202],
    '1491': [26.290896, 63.668812, 30.55358, 65.520825],
    '2311': [28.359233, 61.722067, 31.586707, 63.882425],
    '3009': [21.334245, 60.872001, 23.005903, 62.311346],
    '3187': [27.12113, 60.666674, 30.143968, 61.864314],
    '1697': [26.234466, 60.375013, 27.990683, 61.292594],
    '1965': [19.515862, 59.90263, 21.107221, 60.537411],
    '1006': [9.314004, -0.598873, 10.965376, 1.029305],
    '2405': [8.831667, -2.77797, 10.640765, -0.326369],
    '3703': [-16.81361, 13.065075, -15.80202, 13.444026],
    '332': [-16.72457, 13.384474, -16.629518, 13.4845],
    '2298': [-16.547561, 13.330836, -15.304257, 13.642704],
    '3343': [-6.2434, 34.51478, -3.802688, 35.921641],
    '2446': [-4.123135, 31.714172, -0.998429, 35.437199],
    '911': [-7.752382, 29.476586, -3.112734, 33.097946],
    '3171': [-10.009807, 28.31106, -6.353651, 31.166586],
    '608': [-9.059097, 32.296629, -6.756936, 33.830974],
    '1974': [-9.842343, 30.78595, -7.00207, 32.814245],
    '2697': [-7.125268, 33.14883, -5.315151, 35.015518],
    '1033': [-5.883109, 32.60056, -2.899, 34.88753],
    '401': [-7.321136, 31.338992, -5.241225, 33.482611],
    '559': [37.981743, -14.1599, 40.638825, -10.473842],
    '2197': [36.724612, -16.884551, 40.837525, -13.454676],
    '1940': [32.36725, -21.59173, 34.573234, -16.410133],
    '1953': [31.930637, -26.868069, 33.14016, -24.2085],
    '3146': [33.439848, -21.326148, 36.475098, -16.824228],
    '3385': [30.21555, -17.701472, 35.34935, -13.997732],
    '3787': [35.143452, -18.622429, 39.148973, -14.996636],
    '1685': [43.670496, 41.164907, 45.315071, 41.859276],
    '3304': [44.673392, 41.641784, 44.987878, 41.822977],
    '14': [41.562122, 41.434873, 42.599956, 41.915434],
    '1368': [42.214526, 41.811555, 43.759691, 42.572669],
    '2918': [41.549738, 42.086922, 43.164538, 43.257029],
    '287': [7.51231, 47.461567, 10.487535, 49.791478],
    '373': [8.975926, 47.27009, 13.840547, 50.564225],
    '408': [13.088333, 52.338242, 13.754215, 52.674917],
    '506': [11.265732, 51.361202, 14.765635, 53.558578],
    '514': [8.481735, 53.011021, 8.984185, 53.610028],
    '1239': [8.420749, 53.395274, 10.325959, 53.96406],
    '1282': [7.77336, 49.395552, 10.235999, 51.65745],
    '2017': [10.595047, 53.111556, 14.412267, 54.683955],
    '1853': [6.6575, 51.295232, 11.597785, 53.89232],
    '2319': [5.849338, 50.321091, 9.461421, 52.531443],
    '2743': [6.113387, 48.966571, 8.507987, 50.942335],
    '2806': [6.357666, 49.113971, 7.403773, 49.639438],
    '3020': [11.871463, 50.171923, 15.041585, 51.684974],
    '3021': [10.561227, 50.943062, 13.187072, 53.041551],
    '3025': [8.278556, 53.359831, 11.312514, 55.055443],
    '3403': [9.876976, 50.204819, 12.654115, 51.649008],
    '479': [-2.121742, 7.172056, 0.214976, 8.774835],
    '3704': [-3.109644, 4.744263, -1.43931, 6.135096],
    '945': [-1.246563, 5.724915, 0.295268, 7.196452],
    '2337': [-1.367799, 8.231565, 0.567003, 10.321676],
    '632': [-2.178552, 5.031686, -0.388011, 6.299266],
    '3647': [0.058693, 5.771058, 1.199478, 7.305163],
    '3532': [-2.94234, 9.635936, -1.424702, 11.021375],
    '3527': [-1.573053, 10.278367, 0.034418, 11.174831],
    '1165': [-0.499438, 5.482224, 0.669635, 6.035044],
    '795': [23.517561, 34.803655, 26.317087, 35.694549],
    '254': [22.89213, 35.83542, 24.084477, 38.340297],
    '649': [21.713619, 39.909584, 26.634085, 41.750653],
    '3693': [20.163969, 38.94965, 22.194874, 40.942988],
    '1385': [19.639511, 36.403988, 23.454354, 39.822083],
    '2296': [24.26575, 35.343357, 28.246592, 40.022683],
    '3398': [21.12211, 37.949557, 24.68351, 40.196655],
    '3048': [-52.235365, 61.175632, -21.519121, 71.99223],
    '2825': [-61.701594, 12.052155, -61.604637, 12.173354],
    '3757': [166.921149, -0.546278, 166.930347, -0.540786],
    '127': [-90.820694, 15.11966, -89.410384, 16.074026],
    '3266': [-91.668964, 14.044479, -91.094292, 14.710862],
    '714': [-89.7204, 14.406186, -89.130771, 14.949839],
    '2814': [-90.879998, 14.383184, -90.602888, 14.721912],
    '1187': [-90.759568, 14.256637, -90.203484, 14.915145],
    '1421': [-89.626863, 15.068276, -88.233002, 15.965146],
    '2687': [-92.140015, 14.492971, -91.403557, 15.224424],
    '998': [-91.535655, 13.912005, -90.509064, 14.47157],
    '2639': [86.155925, 26.347837, 88.202877, 28.113061],
    '2640': [84.48389, 26.423814, 87.0143, 27.463162],
    '292': [83.918773, 26.919143, 86.572763, 28.386285],
    '2641': [82.877637, 27.437823, 85.197873, 29.330865],
    '1870': [81.059201, 27.331361, 84.027454, 28.869228],
    '3275': [80.058585, 28.394511, 81.808222, 30.226136],
    '769': [-13.67184, 9.501787, -13.46494, 9.753296],
    '439': [-15.66157, 11.795118, -15.538716, 11.944931],
    '851': [-58.314215, 6.084359, -57.877473, 6.826952],
    '3526': [-58.90585, 4.616555, -57.631281, 6.488595],
    '2470': [-73.300631, 18.258201, -71.698317, 18.976249],
    '2283': [-72.687374, 19.254873, -71.970241, 19.879259],
    '317': [35.518834, 31.707714, 35.905516, 32.206168],
    '1388': [35.54669, 32.202713, 36.081422, 32.74704],
    '195': [34.95778, 29.237993, 35.737106, 30.711777],
    '143': [35.658399, 31.264953, 37.235865, 32.077192],
    '3800': [35.868731, 31.502204, 37.801293, 32.213207],
    '3771': [-87.938225, 14.874987, -86.228602, 15.725082],
    '2544': [18.690814, 46.915971, 20.087811, 48.058865],
    '3303': [21.034242, 47.581475, 22.890595, 48.414911],
    '1230': [20.76436, 46.933644, 22.074133, 47.94641],
    '489': [20.031401, 47.664213, 22.117265, 48.57371],
    '1283': [19.542927, 47.416658, 20.708362, 48.151196],
    '388': [20.392149, 46.262127, 21.665682, 47.213081],
    '803': [19.657788, 46.132919, 20.784625, 46.782065],
    '1438': [19.645682, 46.721876, 20.962531, 47.678947],
    '1629': [17.889966, 47.324646, 18.918428, 47.811136],
    '1213': [16.456706, 47.319751, 17.950407, 48.013095],
    '3584': [16.127299, 46.710619, 17.331815, 47.452966],
    '3785': [16.385396, 46.289193, 17.465704, 47.03088],
    '3608': [17.119473, 46.727486, 18.241214, 47.488092],
    '340': [17.446616, 45.747003, 18.859225, 46.371355],
    '3157': [16.909905, 45.859686, 18.230264, 46.95171],
    '3438': [18.083823, 46.098108, 19.015629, 46.821322],
    '285': [18.740856, 45.917373, 20.137511, 47.113215],
    '1030': [18.041556, 46.647382, 18.970899, 47.574664],
    '596': [-22.125455, 63.977412, -21.182677, 64.384243],
    '2333': [-19.044164, 64.630935, -14.53016, 66.536776],
    '3173': [-21.913085, 63.393551, -17.30299, 64.97304],
    '311': [114.431878, -8.849153, 115.711016, -8.061589],
    '3695': [115.825386, -9.107361, 119.184772, -8.08125],
    '335': [105.109998, -6.997109, 106.780013, -5.879347],
    '645': [108.611985, -8.211816, 111.676892, -6.404166],
    '3690': [106.37083, -7.820708, 108.886819, -5.915664],
    '647': [110.73447, -3.543411, 115.848351, 0.795408],
    '3186': [114.351906, -4.937083, 116.553103, -1.303257],
    '3691': [108.844582, -3.037084, 114.206948, 2.081305],
    '657': [119.423149, -3.274583, 123.896544, 1.355487],
    '1142': [121.160768, 0.306175, 123.551923, 1.03133],
    '2324': [123.114023, 0.291577, 126.910121, 5.565996],
    '3198': [119.353723, -7.332465, 121.791319, -1.882483],
    '3203': [120.869583, -6.035252, 124.082053, -2.710247],
    '3699': [118.75752, -3.568779, 119.907639, -0.861188],
    '13': [95.014481, 2.009867, 98.28628, 5.907235],
    '398': [101.02486, -5.508036, 103.78067, -2.277136],
    '1435': [101.122696, -2.770431, 104.489371, -0.746252],
    '1748': [103.593361, -5.942616, 105.91378, -3.724454],
    '2745': [100.072346, -1.120413, 103.809555, 2.555596],
    '3700': [98.596376, -3.348264, 101.88475, 0.895039],
    '3199': [102.063416, -4.920098, 106.087059, -1.630826],
    '2325': [97.05955, -0.638334, 100.440196, 4.299254],
    '942': [118.931221, -10.931679, 125.192625, -8.068778],
    '1922': [125.78942, -8.345416, 134.90799, -2.779334],
    '2315': [124.304091, -2.476537, 129.567917, 2.642981],
    '937': [110.897211, -8.780454, 115.572914, -5.720417],
    '329': [105.109678, -3.266278, 109.270629, -1.182496],
    '2498': [134.210545, -9.125207, 141.019444, -0.621278],
    '3696': [129.720111, -4.255648, 135.252567, -0.007917],
    '938': [113.836326, -2.418735, 118.987311, 2.608678],
    '2310': [114.56412, 1.046002, 117.997241, 4.407675],
    '3769': [110.003481, -8.204191, 110.838677, -7.546641],
    '1429': [106.685587, -6.371554, 106.973945, -6.089906],
    '2012': [50.359824, 35.761361, 54.130562, 36.965836],
    '2313': [55.908146, 36.579444, 58.422986, 38.300161],
    '1568': [54.336682, 26.483106, 59.570796, 31.960163],
    '1969': [48.949763, 33.384088, 51.050974, 35.570809],
    '1569': [45.407661, 33.690916, 48.105003, 35.28328],
    '2666': [48.724748, 35.402709, 50.856515, 36.815558],
    '3792': [47.174115, 35.551078, 49.433672, 37.251683],
    '1395': [49.637577, 30.701253, 55.497844, 34.510984],
    '3682': [44.032639, 35.964772, 47.392185, 39.782462],
    '208': [47.287172, 37.103059, 48.922255, 39.708781],
    '1129': [53.969422, 36.491659, 56.352017, 38.127837],
    '2724': [56.227974, 33.856312, 61.394708, 37.705281],
    '3189': [55.386452, 30.516795, 60.945903, 35.096072],
    '3121': [58.831036, 25.060389, 63.332056, 31.486519],
    '3762': [52.8005, 29.596223, 56.654564, 33.364924],
    '1306': [52.737921, 25.403821, 59.241743, 28.881484],
    '1588': [47.664111, 29.945444, 50.549566, 32.997128],
    '3372': [50.352348, 34.865239, 53.156903, 36.140103],
    '2149': [43.273733, 32.180892, 44.250454, 32.79774],
    '149': [42.935751, 29.864157, 44.775334, 32.358787],
    '279': [43.942411, 32.13206, 45.157952, 33.293171],
    '290': [44.125233, 33.230179, 44.598567, 33.494494],
    '362': [46.575647, 29.129548, 48.457413, 31.285295],
    '2266': [41.215106, 34.92292, 44.259473, 37.06983],
    '919': [42.349251, 36.685623, 44.281585, 37.380746],
    '2898': [42.419728, 33.433314, 44.92394, 35.738326],
    '1610': [43.224947, 34.743831, 44.826468, 35.881502],
    '206': [43.314705, 35.504492, 45.070059, 37.281338],
    '3280': [44.490069, 34.660868, 46.274159, 36.534722],
    '3519': [-8.805743, 53.765574, -6.549729, 55.382056],
    '1769': [-8.083874, 52.123188, -5.998091, 54.113874],
    '2154': [-10.482109, 51.447641, -6.951083, 53.167579],
    '771': [-10.259086, 52.662005, -7.584171, 54.474368],
    '2515': [73.405781, 50.013436, 79.339058, 54.457547],
    '3807': [69.06711, 42.320124, 75.823518, 46.007263],
    '2675': [59.993452, 48.170279, 68.09202, 54.716753],
    '1937': [50.231782, 41.251587, 56.744065, 46.520131],
    '1527': [62.950089, 45.99961, 77.702277, 51.309786],
    '2695': [59.030602, 42.322707, 68.060302, 47.827726],
    '939': [76.589996, 45.535147, 87.31541, 51.708661],
    '197': [53.630538, 45.177496, 64.277174, 51.317069],
    '256': [47.050375, 46.012055, 56.471824, 49.240349],
    '3084': [65.998979, 40.568688, 70.943305, 46.000535],
    '196': [65.352417, 50.039188, 74.260038, 53.680021],
    '2312': [65.963115, 52.20696, 74.034961, 55.441436],
    '124': [76.738277, 43.032844, 77.166726, 43.401226],
    '369': [46.492161, 47.920497, 54.565237, 51.775899],
    '2382': [71.222171, 51.000677, 71.78511, 51.350943],
    '3498': [33.994409, 0.918774, 36.723713, 5.033421],
    '3455': [34.582535, 0.804289, 35.359627, 1.284199],
    '3508': [34.850304, 0.008547, 35.589626, 0.944354],
    '1741': [36.183838, -0.296194, 37.395107, 0.8694],
    '2039': [37.0881, -0.216473, 38.420704, 0.667437],
    '2191': [35.412468, -1.155907, 36.595409, 0.234663],
    '1616': [34.411427, -0.415976, 35.342213, 0.023905],
    '2387': [36.601265, -0.643567, 37.307785, 0.009813],
    '1615': [34.618484, -0.973188, 35.012959, -0.501332],
    '1590': [36.488228, -1.315209, 37.36282, -0.758492],
    '1882': [36.87598, -1.780179, 37.868122, -0.77365],
    '1493': [36.000206, -3.185665, 37.939404, -1.049367],
    '2181': [36.65987, -1.44406, 37.104397, -1.160461],
    '1600': [39.092285, -3.992775, 40.240807, -2.310056],
    '3322': [37.585275, -4.137199, 39.217808, -2.679865],
    '2095': [39.566284, -4.155029, 39.76145, -3.918701],
    '1111': [-171.723412, -2.870316, 173.164375, 1.634617],
    '2658': [125.574997, 38.893688, 126.388893, 39.283607],
    '1078': [126.981353, 36.611745, 129.471202, 38.618279],
    '1210': [126.369151, 36.878938, 127.831092, 38.205882],
    '736': [126.130951, 35.98708, 127.619012, 37.008368],
    '1376': [126.357025, 37.380544, 126.795806, 37.552023],
    '1446': [126.418224, 35.276633, 127.918528, 36.132627],
    '1447': [126.115733, 34.289374, 127.801854, 35.462022],
    '1212': [127.617461, 34.70437, 129.265715, 35.88706],
    '549': [128.96315, 35.081244, 129.197781, 35.270767],
    '1211': [127.799983, 35.530183, 130.902924, 37.537962],
    '3046': [126.793739, 37.429473, 127.192061, 37.680801],
    '817': [127.21899, 36.23321, 127.482276, 36.479066],
    '3032': [127.211698, 35.97522, 128.650267, 37.218271],
    '1209': [126.822885, 35.150077, 127.005819, 35.254593],
    '2635': [20.760856, 42.445065, 21.552794, 43.138252],
    '3644': [18.989243, 44.629595, 20.362601, 45.208139],
    '656': [20.399302, 43.7955, 21.183113, 44.442069],
    '985': [55.448057, -4.623004, 55.460086, -4.604266],
    '3217': [-12.992491, 6.924195, -11.142781, 8.488779],
    '3707': [-13.291839, 8.171584, -12.913335, 8.499616],
    '1792': [6.626996, 43.778751, 11.427646, 46.632517],
    '1051': [9.197912, 43.731924, 13.918546, 47.091757],
    '1766': [9.6867, 40.879404, 14.027521, 44.472707],
    '192': [13.018547, 37.915754, 18.520248, 42.895081],
    '3006': [8.13125, 36.64956, 15.652828, 41.259236],
    '3244': [-77.996332, 18.206188, -77.736237, 18.524863],
    '750': [-77.494173, 17.705717, -77.125341, 18.209802],
    '2829': [-77.219272, 17.839535, -76.848491, 18.250922],
    '1932': [-77.63845, 17.838683, -77.345998, 18.244363],
    '2452': [135.093607, 34.271821, 135.746589, 35.051263],
    '2412': [130.82475, 32.73593, 132.085254, 33.692433],
    '1325': [134.252845, 34.188735, 135.468524, 35.674556],
    '1056': [135.449339, 35.343687, 136.832362, 36.295355],
    '3072': [135.763739, 34.790571, 136.454998, 35.703726],
    '2207': [135.539698, 33.858956, 136.229777, 34.781332],
    '1109': [136.276216, 35.133729, 137.653054, 36.465066],
    '2056': [135.853184, 33.722858, 136.927319, 35.257649],
    '3665': [135.05711, 33.43539, 136.013364, 34.384482],
    '3434': [138.942865, 24.2812, 153.986531, 35.898424],
    '1057': [130.039571, 33.001439, 131.190574, 33.964554],
    '2892': [138.711419, 35.753384, 139.900333, 36.283344],
    '1058': [139.164748, 36.791337, 141.042114, 37.976647],
    '3430': [139.326559, 36.199682, 140.292198, 37.154981],
    '187': [139.860377, 40.21776, 141.683311, 41.546276],
    '2818': [129.758967, 32.950524, 130.542127, 33.556416],
    '2179': [128.598254, 32.56932, 130.38012, 34.706644],
    '2083': [130.703377, 31.360834, 131.885707, 32.83894],
    '1675': [129.967262, 32.094924, 131.329506, 33.195122],
    '1489': [128.520048, 27.328753, 131.205448, 32.229383],
    '1204': [138.397128, 35.985333, 139.669944, 37.058627],
    '1698': [134.854035, 34.705754, 136.055473, 35.7793],
    '705': [139.754578, 34.899639, 140.869583, 36.104101],
    '1329': [139.687691, 35.739582, 140.845383, 36.945266],
    '1510': [138.915767, 35.136361, 139.787048, 35.672897],
    '1290': [132.036448, 34.079201, 133.45639, 35.105683],
    '3448': [133.136098, 35.057678, 134.515315, 35.609051],
    '3073': [131.667966, 34.302439, 133.385843, 36.348916],
    '2263': [137.634981, 36.736559, 139.899887, 38.553587],
    '2178': [137.324551, 35.198414, 138.739252, 37.030423],
    '54': [136.671033, 34.577901, 137.838115, 35.424776],
    '3749': [138.180144, 35.168384, 139.134379, 35.971709],
    '2082': [140.274979, 37.773256, 141.675519, 39.002779],
    '3746': [139.539605, 37.733829, 140.646436, 39.133357],
    '70': [139.693108, 38.873163, 140.995425, 40.511069],
    '1414': [140.653208, 38.747651, 142.070202, 40.450301],
    '3451': [136.768438, 36.274372, 137.763381, 36.979749],
    '3078': [137.474107, 34.59539, 139.148003, 35.64594],
    '1396': [136.244052, 36.067229, 137.360404, 37.533427],
    '2413': [133.266927, 34.429538, 134.413161, 35.3529],
    '3433': [133.660748, 33.545613, 134.750102, 34.239743],
    '1624': [132.614865, 32.721584, 134.306833, 33.883363],
    '964': [132.01709, 32.897733, 133.692777, 34.299412],
    '1487': [133.557001, 34.012327, 134.440696, 34.564601],
    '2414': [122.933736, 24.255248, 131.27339, 27.094758],
    '1298': [139.405206, 41.397809, 145.817103, 45.522796],
    '3747': [130.86203, 33.769003, 132.47201, 34.681045],
    '437': [73.057557, 41.832163, 77.226928, 43.266797],
    '3618': [101.386242, 17.813537, 102.898445, 19.413567],
    '2753': [23.945543, 56.857346, 24.324244, 57.086065],
    '414': [35.468537, 33.864344, 35.540226, 33.904446],
    '1788': [35.6485, 34.137132, 36.287326, 34.514313],
    '2106': [35.38678, 33.489473, 35.899518, 33.978146],
    '3183': [35.107074, 33.085197, 35.635987, 33.593693],
    '404': [35.615826, 33.414193, 36.089183, 33.932798],
    '1986': [27.294665, -29.96271, 28.31438, -29.282079],
    '2114': [-10.95846, 6.21782, -10.367941, 6.826793],
    '2192': [9.392101, 28.544899, 11.95186, 32.410055],
    '2077': [13.397663, 29.37676, 16.531219, 32.439706],
    '3472': [13.076339, 32.333737, 13.954436, 32.875713],
    '328': [19.926635, 31.006997, 21.31904, 32.538688],
    '997': [9.487877, 47.186828, 9.579003, 47.229215],
    '3022': [9.485929, 47.060775, 9.62467, 47.194227],
    '3553': [9.495432, 47.087057, 9.611678, 47.194039],
    '3628': [24.38504, 54.126911, 26.758904, 55.517724],
    '2495': [23.879556, 55.387223, 26.045951, 56.450238],
    '1547': [22.669957, 54.455405, 24.811499, 55.576743],
    '133': [23.31176, 53.896795, 25.025632, 54.562617],
    '1618': [20.953823, 55.180571, 22.024763, 56.385268],
    '3086': [22.466274, 55.464442, 24.182323, 56.41485],
    '2037': [5.985933, 49.681343, 6.279539, 49.834442],
    '594': [5.848413, 49.54619, 6.104185, 49.723724],
    '1875': [5.998434, 49.516975, 6.309687, 49.692116],
    '870': [5.97019, 49.785378, 6.314253, 49.955254],
    '996': [5.810578, 49.447854, 6.240651, 49.589825],
    '2153': [7.409056, 43.72476, 7.439782, 43.751343],
    '3516': [106.356383, 47.290262, 108.484795, 48.257157],
    '2586': [18.989402, 42.178961, 19.655049, 42.708957],
    '1150': [21.149874, 41.717966, 21.821308, 42.24919],
    '867': [47.859391, -14.265099, 49.665679, -11.953417],
    '3563': [45.772282, -20.28289, 47.855637, -19.135768],
    '251': [47.647736, -20.47019, 49.520139, -17.484211],
    '176': [45.200933, -25.206939, 47.417118, -22.666956],
    '460': [44.891374, -17.272274, 47.364504, -15.199083],
    '249': [43.220818, -25.28914, 45.777157, -20.892445],
    '3528': [45.503445, -22.238323, 47.458124, -20.702589],
    '653': [32.674211, -15.329226, 34.97441, -12.162823],
    '3219': [34.250618, -17.129522, 35.918573, -13.483111],
    '2353': [32.943006, -14.42775, 35.29625, -9.367227],
    '3033': [100.809757, 2.600389, 101.970747, 3.870185],
    '1465': [102.470883, 1.270148, 104.294639, 2.896562],
    '1670': [101.615178, 3.033554, 101.7592, 3.24319],
    '2026': [101.971094, 2.108722, 102.592913, 2.49808],
    '2224': [101.703722, 2.390827, 102.700179, 3.287777],
    '2809': [115.341337, 4.114879, 119.269057, 7.359583],
    '3005': [109.538352, 0.853946, 115.677787, 4.990089],
    '1560': [101.33398, 4.55917, 102.663263, 6.243836],
    '2655': [101.659688, 2.879796, 101.732682, 2.976052],
    '3382': [102.375585, 3.932839, 103.492125, 5.938368],
    '2482': [101.334633, 2.457941, 103.638118, 4.79231],
    '1556': [99.640984, 5.076828, 101.127939, 6.543583],
    '2539': [100.120879, 6.254587, 100.378147, 6.726082],
    '2537': [100.376198, 3.668114, 101.752276, 5.934363],
    '2532': [100.178719, 5.121971, 100.550617, 5.585678],
    '1726': [115.15724, 5.252056, 115.26664, 5.392787],
    '1919': [73.526238, 4.200846, 73.526253, 4.200962],
    '322': [-8.068237, 12.50289, -7.912268, 12.711431],
    '1081': [-1.609463, 14.94407, 4.244649, 19.036584],
    '3031': [-7.081341, 12.549442, -3.958407, 15.551256],
    '2857': [14.481333, 35.908579, 14.495805, 35.926936],
    '3358': [14.491218, 35.905813, 14.513532, 35.917991],
    '3574': [14.504862, 35.892603, 14.5195, 35.90362],
    '1706': [14.471978, 35.888431, 14.494076, 35.90407],
    '1349': [14.396231, 35.899249, 14.434567, 35.929213],
    '1907': [171.25139, 7.05894, 171.384016, 7.094362],
    '825': [-17.066446, 19.408194, -14.173149, 21.333929],
    '446': [57.308653, -20.488743, 57.473283, -20.164227],
    '2091': [57.47096, -20.33695, 57.668495, -20.166948],
    '2572': [57.438249, -20.421097, 57.600395, -20.182531],
    '2604': [57.471907, -20.199599, 57.555989, -20.138554],
    '299': [-118.363198, 28.0, -112.763179, 32.718768],
    '300': [-115.081701, 22.871959, -109.415477, 28.0],
    '576': [-92.466694, 17.815693, -89.121229, 20.848327],
    '704': [-94.139156, 14.535749, -90.371542, 17.983957],
    '708': [-109.074886, 25.563548, -103.299356, 31.783909],
    '753': [-103.960002, 24.542684, -99.843258, 29.880023],
    '759': [-114.759326, 18.344334, -103.486346, 19.512519],
    '2044': [-99.364924, 19.048237, -98.940303, 19.592757],
    '921': [-107.210132, 22.346032, -102.472697, 26.844876],
    '1180': [-102.096899, 19.91275, -99.671303, 21.839417],
    '1196': [-102.184117, 16.316055, -98.007276, 18.887271],
    '1285': [-99.859106, 19.597891, -97.985043, 21.398518],
    '1433': [-105.695249, 18.926132, -101.510542, 22.75016],
    '2043': [-100.613154, 18.367434, -98.596867, 20.285867],
    '2048': [-103.738257, 17.92718, -100.063359, 20.394436],
    '2125': [-99.494414, 18.332567, -98.632947, 19.131702],
    '2218': [-106.687452, 20.603221, -103.720896, 23.084082],
    '2375': [-101.206763, 23.162683, -98.421576, 27.799137],
    '2394': [-98.552529, 15.658296, -93.867427, 18.669317],
    '2646': [-99.069968, 17.860912, -96.724683, 20.83996],
    '2686': [-100.595833, 20.019168, -99.04308, 21.670005],
    '2691': [-89.296562, 17.894346, -86.713609, 21.591254],
    '2938': [-102.296038, 21.160184, -98.325967, 24.491522],
    '3107': [-109.447693, 22.47234, -105.392245, 27.042048],
    '3161': [-115.053022, 26.302968, -108.424271, 32.493913],
    '3308': [-94.129754, 17.250967, -90.987619, 18.649873],
    '3334': [-100.14495, 22.207958, -97.145007, 27.679063],
    '3424': [-98.708399, 19.105072, -97.625674, 19.728917],
    '3602': [-98.681547, 17.136965, -93.60794, 22.471657],
    '3773': [-90.406265, 19.551174, -87.533145, 21.624266],
    '3779': [-104.353533, 21.042428, -100.742324, 25.125236],
    '2590': [158.111318, 6.197624, 160.715363, 7.011352],
    '718': [28.643865, 46.859898, 29.121906, 47.154896],
    '3458': [28.499128, 46.54663, 29.974153, 48.173349],
    '2760': [-85.190697, 10.708355, -83.660644, 11.881853],
    '1927': [-86.674928, 11.720142, -85.794342, 12.641022],
    '1776': [-87.119041, 11.990396, -86.273205, 13.128073],
    '1152': [-86.133218, 11.602202, -85.670371, 12.214578],
    '866': [51.98423, 16.650442, 56.356093, 20.0],
    '77': [56.203785, 23.108007, 58.072724, 24.980934],
    '21': [55.209207, 17.475732, 58.96125, 26.383578],
    '2164': [58.0162, 22.889691, 59.224306, 23.71125],
    '3204': [57.913003, 20.350287, 59.837244, 23.303718],
    '19': [56.595079, 20.873098, 58.337737, 23.542853],
    '316': [60.88141, 24.914246, 70.252215, 31.986358],
    '3108': [66.689928, 23.769527, 71.096551, 28.470585],
    '272': [73.396857, 32.797655, 75.494727, 35.085266],
    '1589': [69.2378, 31.070376, 74.115107, 36.857514],
    '2651': [69.403261, 27.705548, 75.376991, 34.06215],
    '1399': [72.885265, 33.493716, 73.26997, 33.810982],
    '1640': [134.342177, 7.135921, 134.513948, 7.36262],
    '457': [-82.936078, 8.816004, -82.00464, 9.618039],
    '762': [-80.883742, 8.626625, -79.080225, 9.632416],
    '1281': [-80.955097, 7.531218, -80.382406, 8.154777],
    '2493': [-80.179346, 8.408611, -79.562037, 9.186001],
    '2492': [-79.698102, 8.208701, -78.057445, 9.480551],
    '3603': [-81.88767, 7.204726, -80.633696, 8.88731],
    '716': [-83.051312, 7.99874, -81.530136, 8.922259],
    '756': [-80.852892, 8.099602, -80.044729, 9.024966],
    '2214': [147.075541, -9.531128, 147.29499, -9.351941],
    '3714': [143.78581, -6.245195, 144.518727, -5.212685],
    '770': [-57.995072, -23.509491, -56.126165, -22.068984],
    '778': [-57.478317, -25.668976, -56.561363, -24.843279],
    '555': [-56.856444, -25.706998, -54.923371, -24.465456],
    '1408': [-57.045722, -27.606891, -54.648338, -26.092172],
    '131': [-55.55064, -26.257108, -54.255387, -24.476527],
    '633': [-57.826763, -26.025117, -57.193009, -25.096463],
    '2652': [-71.028318, -17.314468, -68.830879, -13.012996],
    '3493': [-81.044147, -4.208549, -80.12676, -3.414288],
    '2570': [-81.326638, -6.356228, -79.231685, -4.072883],
    '1745': [-80.639063, -7.122381, -79.1368, -5.507995],
    '566': [-79.456531, -7.729049, -77.760343, -4.615637],
    '140': [-78.714918, -6.97447, -77.236954, -2.992343],
    '1711': [-79.686715, -8.945984, -76.913691, -6.934808],
    '156': [-78.648425, -10.734062, -76.763637, -8.05639],
    '2941': [-77.789522, -8.730912, -75.496834, -5.377544],
    '1314': [-77.321812, -10.465696, -74.500706, -8.314093],
    '2508': [-76.734828, -11.125934, -74.126148, -9.430794],
    '1797': [-77.896805, -13.332225, -75.521651, -10.286051],
    '1796': [-77.203425, -12.418848, -76.63222, -11.583484],
    '3511': [-75.953525, -11.428099, -70.532183, -7.278778],
    '1473': [-76.49312, -12.66572, -73.365994, -10.665014],
    '1331': [-76.280712, -15.424078, -74.669228, -13.006228],
    '1890': [-72.3995, -13.373027, -68.652214, -9.945431],
    '810': [-73.979162, -15.44436, -70.394197, -11.238475],
    '194': [-73.843238, -14.743038, -72.080798, -13.237347],
    '267': [-75.114773, -15.606529, -72.833572, -12.183471],
    '211': [-75.095082, -17.296037, -70.795559, -14.662927],
    '2118': [-71.474683, -17.824667, -69.991388, -16.015405],
    '3313': [-71.135612, -18.352311, -69.473252, -16.789571],
    '1836': [-77.825007, -8.700888, -69.944855, -0.038777],
    '262': [118.419896, 4.645417, 125.007743, 8.163487],
    '779': [120.444614, 16.180256, 121.647231, 18.544093],
    '2042': [120.906395, 14.35173, 121.135036, 14.785292],
    '1364': [119.749722, 15.61777, 120.976454, 18.651218],
    '562': [120.768966, 15.754419, 122.526253, 21.121818],
    '648': [119.869188, 14.408722, 122.24122, 16.509255],
    '567': [120.567662, 13.164556, 122.792518, 15.216889],
    '2062': [116.928864, 7.803903, 122.697113, 13.864622],
    '3788': [121.897519, 6.559809, 123.666222, 8.727535],
    '415': [122.298648, 11.72125, 124.42013, 14.349215],
    '3720': [121.843222, 9.444286, 123.565414, 11.93914],
    '658': [122.589096, 9.039583, 124.594871, 11.301211],
    '958': [124.260865, 9.910389, 125.819382, 12.585354],
    '2347': [123.553827, 7.400869, 125.443482, 9.258009],
    '838': [125.04679, 5.365759, 126.604569, 8.003247],
    '3143': [124.017046, 5.568641, 125.538566, 7.638289],
    '599': [125.169796, 7.919278, 126.457085, 10.471595],
    '3264': [21.140003, 49.002047, 23.547838, 50.820548],
    '2587': [21.594231, 52.262022, 23.949764, 54.410403],
    '1819': [18.074596, 50.843277, 20.659258, 52.394005],
    '1855': [14.817364, 50.096601, 17.795269, 51.804754],
    '2427': [16.907901, 49.972575, 18.695487, 51.194569],
    '3300': [19.704304, 50.186031, 21.868826, 51.342452],
    '2013': [19.259214, 51.013137, 23.128409, 53.481806],
    '1166': [15.776287, 51.103783, 19.104011, 53.655871],
    '1781': [19.083201, 49.178764, 21.420741, 50.520488],
    '1864': [14.534181, 51.363189, 16.41684, 53.12391],
    '1672': [17.247966, 52.331686, 19.761595, 53.781075],
    '2598': [16.699077, 53.490964, 19.647215, 54.835652],
    '3698': [14.122936, 52.62472, 16.982078, 54.570034],
    '1862': [21.615546, 50.251838, 24.145838, 52.287841],
    '3672': [19.12764, 53.139114, 22.805845, 54.442394],
    '3102': [18.035036, 49.393978, 19.974066, 51.09943],
    '1886': [-17.26375, 32.403876, -16.285662, 33.104409],
    '265': [-8.783276, 40.278817, -8.089172, 41.080154],
    '502': [-8.811574, 41.318798, -7.810677, 41.819816],
    '503': [-7.431892, 41.024542, -6.189285, 41.992236],
    '613': [-8.293123, 39.537365, -6.863996, 40.416132],
    '1024': [-8.995444, 36.980653, -7.399969, 37.529096],
    '1185': [-7.849227, 40.229919, -6.781202, 41.179328],
    '1770': [-9.407092, 39.211577, -8.108038, 40.089736],
    '1808': [-9.500429, 38.67323, -8.781861, 39.317729],
    '2609': [-8.789063, 41.00138, -7.875579, 41.471971],
    '2980': [-9.00155, 38.73149, -7.808988, 39.838729],
    '879': [-9.26275, 37.749393, -8.129696, 38.844295],
    '3610': [-8.881058, 41.606381, -8.082765, 42.153925],
    '3637': [-8.359447, 40.322638, -7.304573, 41.214658],
    '115': [22.672433, 45.455563, 24.250921, 46.569008],
    '200': [20.705293, 45.901841, 22.748715, 46.680817],
    '212': [24.4215, 44.376874, 25.325817, 45.611184],
    '284': [25.972844, 46.016832, 27.510103, 46.828602],
    '418': [21.427815, 46.384998, 22.818761, 47.60008],
    '441': [23.918737, 46.746362, 25.093359, 47.608333],
    '504': [27.066355, 44.749662, 28.197668, 45.492315],
    '508': [24.639276, 45.381375, 26.10422, 46.19042],
    '553': [26.072182, 44.734266, 27.44173, 45.802443],
    '600': [21.353054, 44.58533, 22.712712, 45.668884],
    '752': [22.637293, 46.396756, 24.226156, 47.379708],
    '773': [27.275805, 43.733744, 28.993845, 44.804108],
    '793': [25.450561, 45.516366, 26.44562, 46.27699],
    '827': [25.124821, 44.403355, 25.99191, 45.440815],
    '898': [22.826907, 43.699603, 24.26466, 44.728161],
    '1073': [27.226099, 45.390246, 28.232929, 46.163383],
    '1136': [22.560144, 44.568343, 23.855599, 45.353073],
    '1248': [24.853673, 46.102092, 26.292917, 47.183965],
    '1321': [22.336098, 45.253856, 23.60167, 46.352816],
    '1326': [26.301876, 44.33411, 28.108989, 44.857032],
    '1328': [26.493497, 46.81935, 28.126465, 47.589468],
    '1359': [25.82369, 44.215601, 26.463456, 44.768908],
    '1958': [22.961415, 47.320658, 25.056366, 48.01982],
    '2024': [22.016149, 44.098527, 23.455034, 45.107659],
    '529': [25.963765, 44.34942, 26.225627, 44.541597],
    '2158': [23.959057, 46.076328, 25.318354, 47.136547],
    '2220': [25.65957, 46.641773, 27.243617, 47.3403],
    '2419': [23.881911, 43.682709, 24.886518, 44.901386],
    '2618': [25.451423, 44.73416, 26.609939, 45.522196],
    '2899': [22.492864, 46.853388, 23.842104, 47.458099],
    '3010': [22.140237, 47.333064, 23.639807, 48.120064],
    '3090': [23.588701, 45.494107, 24.965147, 46.28409],
    '3265': [24.947165, 47.077324, 26.704595, 47.988011],
    '3377': [24.591211, 43.616523, 25.723097, 44.518149],
    '3414': [20.208613, 45.193552, 22.551726, 46.205052],
    '3566': [23.576115, 44.490243, 24.540487, 45.590842],
    '3586': [27.18833, 46.003281, 28.260709, 46.991713],
    '3652': [26.370985, 45.371158, 27.558525, 46.205967],
    '129': [77.889143, 50.639877, 87.16865, 54.45311],
    '3491': [35.899512, 52.946357, 38.951545, 54.851836],
    '1577': [59.204854, 58.580078, 85.976673, 65.717736],
    '1661': [76.098507, 51.773288, 113.92237, 81.27449],
    '3292': [57.229384, 56.053224, 66.184857, 61.946615],
    '3645': [41.168105, 47.447132, 47.43145, 51.247608],
    '1390': [95.647175, 51.129885, 119.138674, 64.321545],
    '2788': [38.220892, 45.951688, 44.324152, 50.214477],
    '2801': [38.665982, 53.30907, 42.691248, 55.366494],
    '1660': [38.684018, 43.760146, 40.775739, 45.21681],
    '2912': [47.935196, 51.773037, 52.568276, 54.677006],
    '3364': [47.258597, 53.974244, 54.265446, 56.678167],
    '3441': [75.056664, 55.605838, 89.378629, 61.035672],
    '1533': [29.306229, 59.898309, 37.946638, 66.667324],
    '218': [35.504731, 60.635816, 69.050066, 81.858534],
    '237': [44.960978, 45.429923, 49.220917, 48.863565],
    '392': [35.325686, 49.797017, 39.280769, 51.429731],
    '526': [31.242105, 51.842114, 35.325127, 54.032761],
    '697': [57.131288, 51.990784, 63.352015, 56.365798],
    '739': [45.911256, 54.627395, 48.420602, 56.328518],
    '3506': [64.827192, 55.14713, 75.199295, 59.989559],
    '2536': [42.081739, 52.291266, 46.980844, 54.030197],
    '2130': [36.803101, 55.142175, 37.96435, 56.021224],
    '2131': [35.148496, 54.255696, 40.205688, 56.958512],
    '2368': [29.618726, 56.909396, 36.228919, 59.432763],
    '2371': [75.08801, 53.29141, 85.115957, 57.237834],
    '2422': [70.358946, 53.435124, 76.303592, 58.575719],
    '2441': [34.826159, 51.934992, 38.067214, 53.652491],
    '2895': [141.627494, 45.575122, 156.510271, 54.42552],
    '2894': [105.509063, 55.487671, 162.855209, 76.75595],
    '3004': [42.50367, 49.801965, 50.83258, 52.819371],
    '2735': [88.791531, 49.74293, 99.273702, 53.72893],
    '3504': [30.728387, 55.635621, 38.326858, 58.863139],
    '3512': [51.124165, 55.850578, 54.436131, 58.545842],
    '1501': [33.445832, 53.277183, 37.27543, 55.35128],
    '3520': [45.797063, 52.532839, 50.242466, 54.891446],
    '3646': [34.719093, 58.477315, 47.152878, 61.614232],
    '3758': [37.327909, 56.534344, 41.193498, 58.956012],
    '1413': [39.374037, 56.352363, 43.324612, 57.739675],
    '1561': [84.447895, 52.162703, 89.402644, 56.83442],
    '1573': [130.386742, 46.634961, 147.216789, 62.530853],
    '1498': [19.638989, 54.318274, 22.886749, 55.2937],
    '2630': [130.395082, 42.295003, 139.031794, 48.464346],
    '359': [53.157542, 51.569554, 60.001809, 56.534644],
    '1597': [29.977339, -2.079772, 30.27715, -1.779657],
    '2349': [29.450093, -1.91296, 30.275624, -1.308865],
    '3487': [-171.920474, -14.043466, -171.695557, -13.79522],
    '2940': [12.414812, 43.914046, 12.457139, 43.944672],
    '3442': [-175.355469, -21.272929, -175.032089, -21.062487],
    '791': [-61.499579, 10.306733, -61.198964, 10.591447],
    '2927': [-61.491725, 10.248005, -61.427847, 10.311957],
    '2606': [-61.54074, 10.635605, -61.491616, 10.678005],
    '3494': [-61.428545, 10.565505, -61.156959, 10.809128],
    '683': [-61.468229, 10.503132, -61.364492, 10.56628],
    '3497': [10.005933, 36.692511, 10.354662, 36.943137],
    '424': [10.042952, 36.447092, 10.417745, 36.810054],
    '1558': [8.253461, 35.615945, 9.170756, 36.453426],
    '3288': [10.1585, 35.396637, 10.684242, 36.391772],
    '2816': [9.711099, 34.168335, 11.317662, 35.278506],
    '1492': [9.269559, 35.02023, 10.307273, 36.138334],
    '1947': [9.56626, 36.552974, 10.119171, 36.971661],
    '2670': [7.742543, 32.502672, 10.011664, 34.185428],
    '3363': [8.35724, 30.051502, 11.341852, 33.234752],
    '2660': [9.247392, 33.269882, 10.47971, 34.282625],
    '336': [8.898594, 36.723892, 10.280991, 37.54168],
    '214': [9.963289, 36.821974, 10.28864, 37.115162],
    '2174': [10.332305, 36.352762, 11.13653, 37.138666],
    '97': [10.493205, 35.423021, 11.048464, 35.787454],
    '1068': [8.041679, 34.05264, 9.56073, 34.810227],
    '1885': [9.70964, 32.263316, 11.599217, 33.90098],
    '233': [56.597758, 35.435569, 61.686066, 40.126947],
    '2999': [6.461356, 0.021755, 6.759908, 0.412939],
    '954': [44.979425, 17.116757, 55.666699, 29.102931],
    '2182': [43.617811, 16.95, 47.74501, 19.531132],
    '2341': [37.889226, 27.525225, 46.05115, 32.23185],
    '1217': [39.364944, 25.290978, 43.927616, 28.89265],
    '2772': [41.97361, 19.253609, 48.309998, 27.672736],
    '1': [41.3825, 17.426106, 44.521942, 20.976109],
    '2016': [38.665841, 18.09521, 43.667496, 23.971943],
    '3311': [34.57182, 24.534894, 40.013227, 29.999803],
    '2020': [36.738424, 22.489166, 42.139068, 27.526557],
    '106': [41.237416, 24.442382, 44.902474, 28.109498],
    '76': [40.793053, 19.362221, 42.041382, 20.766109],
    '1440': [41.585602, 16.379572, 43.3365, 18.328052],
    '87': [37.000897, 28.544766, 41.978627, 31.755351],
    '823': [-17.526906, 14.587993, -17.126192, 14.885859],
    '1522': [-16.381142, 13.591744, -15.414428, 14.498125],
    '2888': [-16.525611, 15.493817, -13.703066, 16.692404],
    '3399': [-17.176505, 14.056722, -16.176272, 15.360191],
    '509': [16.833423, 48.006681, 17.528138, 48.654032],
    '3475': [16.933882, 47.758985, 17.985436, 48.877901],
    '3466': [17.353036, 48.487263, 18.826868, 49.319705],
    '2272': [17.705419, 47.724963, 19.070556, 48.714406],
    '3812': [18.322998, 48.742284, 20.060438, 49.613816],
    '333': [18.478673, 48.053667, 20.469948, 48.947248],
    '2626': [19.884141, 48.76256, 22.56557, 49.46094],
    '1641': [20.181222, 48.33264, 22.387019, 49.019884],
    '1963': [14.1182, 45.47164, 16.600369, 46.905237],
    '1656': [13.651311, 45.454061, 14.76738, 46.5227],
    '1302': [159.939708, -9.461159, 160.028005, -9.422133],
    '325': [45.269264, 1.99211, 45.604037, 2.183094],
    '3728': [43.283871, 8.774661, 46.030395, 10.867346],
    '2376': [47.560928, 7.402434, 50.175618, 9.139525],
    '374': [42.400002, 1.46, 44.689999, 3.92],
    '948': [22.73574, -34.21365, 30.194684, -30.0018],
    '2436': [24.34662, -30.69408, 29.78513, -26.66879],
    '1085': [27.15634, -26.92383, 29.09842, -25.109612],
    '1686': [28.873604, -31.079862, 32.89113, -26.80442],
    '1802': [26.40765, -25.42279, 31.883835, -22.125424],
    '2139': [28.243465, -27.50602, 32.104033, -23.98124],
    '2331': [22.62903, -28.11314, 28.29835, -24.636679],
    '2342': [16.454558, -32.944995, 25.54933, -22.000671],
    '3710': [17.759086, -34.833214, 24.22241, -30.43026],
    '642': [29.797973, 3.491533, 32.173154, 6.251062],
    '582': [-18.160458, 27.637734, -13.41991, 29.416011],
    '2029': [-4.301143, 35.170798, -2.923194, 35.320107],
    '679': [-5.382061, 35.870949, -5.278344, 35.91755],
    '1718': [-3.134271, 41.919034, -1.678715, 42.644265],
    '361': [-3.450147, 42.47237, -1.729369, 43.456955],
    '2157': [-2.344411, 37.373753, -0.688585, 38.755085],
    '1891': [-4.579076, 39.884719, -3.052983, 41.165845],
    '1075': [-9.301329, 41.808481, -6.733953, 43.790561],
    '1009': [-7.544841, 37.941026, -4.647577, 40.486611],
    '3568': [-1.528867, 37.843779, 0.514701, 40.788631],
    '619': [0.159181, 40.523043, 3.320417, 42.861419],
    '615': [-5.406184, 38.022489, -0.915793, 41.327632],
    '614': [-7.076486, 40.08245, -1.775372, 43.238188],
    '590': [-4.851659, 42.75805, -3.149635, 43.513656],
    '310': [1.213725, 38.831078, 4.327739, 40.089539],
    '238': [-7.182396, 42.882543, -4.510594, 43.658779],
    '202': [-2.173671, 39.846778, 0.771307, 42.924495],
    '157': [-7.522867, 36.000393, -1.630124, 38.729087],
    '2351': [79.652031, 8.522188, 80.961869, 9.832084],
    '956': [80.753043, 6.51073, 81.878778, 8.992083],
    '634': [80.422148, 6.751222, 81.015984, 8.012348],
    '2302': [79.952793, 7.646962, 81.340142, 8.920218],
    '2327': [79.695389, 7.258218, 80.577822, 8.567109],
    '2811': [80.145837, 6.228175, 80.952414, 7.396529],
    '3206': [79.988895, 5.919058, 81.707882, 6.580041],
    '3548': [80.775942, 6.296472, 81.637151, 7.624962],
    '3705': [79.8169, 6.326073, 80.381271, 7.330529],
    '2841': [-62.755122, 17.212847, -62.624527, 17.325635],
    '617': [-61.033442, 13.86473, -60.93685, 14.0387],
    '2840': [-61.237194, 13.122574, -61.147083, 13.210068],
    '88': [32.353516, 13.564697, 34.285522, 15.467896],
    '1579': [31.697875, 15.184082, 34.380676, 16.630127],
    '2729': [33.258484, 17.001526, 38.580482, 23.13872],
    '2503': [-55.245197, 5.766554, -55.088965, 5.910223],
    '1952': [30.7908, -26.89092, 31.70537, -26.16167],
    '1284': [30.898487, -26.48192, 31.765124, -25.718798],
    '3556': [14.798499, 62.150123, 19.287079, 64.002274],
    '3555': [14.331157, 63.407572, 21.61653, 66.400357],
    '1427': [11.975325, 61.557666, 17.034089, 65.291039],
    '1067': [14.472934, 60.186785, 17.51662, 62.348347],
    '826': [12.137911, 59.851647, 16.71626, 62.267493],
    '3533': [16.699968, 59.406372, 18.63365, 60.660032],
    '3557': [15.430857, 59.209951, 16.98257, 60.185446],
    '2391': [14.288826, 58.673617, 15.806078, 60.105757],
    '3554': [11.691254, 58.732144, 14.474656, 61.052123],
    '3558': [10.981775, 57.159675, 14.779656, 59.260648],
    '1235': [11.902772, 56.327755, 13.699175, 57.598185],
    '3124': [12.446441, 55.336954, 14.5836, 56.533285],
    '1667': [13.305343, 56.365168, 15.840253, 57.243829],
    '449': [14.37657, 55.991168, 16.060447, 56.506436],
    '1499': [15.328914, 56.19471, 17.150478, 58.154636],
    '1463': [13.080273, 56.88236, 15.664522, 58.195399],
    '3253': [17.241065, 58.792976, 19.08491, 60.22248],
    '2392': [14.420048, 57.703886, 16.948876, 59.033916],
    '3145': [15.622016, 58.60997, 17.629101, 59.542672],
    '2291': [15.377227, 65.065446, 24.152359, 69.05997],
    '1144': [18.092417, 56.907055, 19.351681, 57.999807],
    '5': [7.713528, 47.137478, 8.453736, 47.620961],
    '190': [9.192671, 47.247022, 9.630894, 47.469037],
    '191': [9.309794, 47.233993, 9.617833, 47.438506],
    '358': [7.325275, 47.337884, 7.961839, 47.564367],
    '357': [7.554659, 47.519297, 7.691646, 47.599591],
    '409': [6.861491, 46.326471, 8.455163, 47.345313],
    '1049': [6.741869, 46.437938, 7.379055, 47.006806],
    '1094': [5.956093, 46.12855, 6.310751, 46.364578],
    '1122': [8.872993, 46.796485, 9.252585, 47.173987],
    '1169': [8.651067, 46.169601, 10.490866, 47.065148],
    '1474': [6.841208, 47.150409, 7.558345, 47.504131],
    '1866': [7.836884, 46.774989, 8.514062, 47.286888],
    '2228': [6.434723, 46.846593, 7.087653, 47.165634],
    '2260': [8.218102, 46.771514, 8.574944, 47.019954],
    '2396': [8.042318, 46.753169, 8.505796, 46.980339],
    '3023': [8.404426, 47.552387, 8.875966, 47.807952],
    '3026': [8.388922, 46.885404, 9.004724, 47.222563],
    '3155': [7.340416, 47.074384, 8.031376, 47.502701],
    '2834': [8.795722, 46.872883, 9.674139, 47.547199],
    '3402': [8.667987, 47.375915, 9.502981, 47.695318],
    '3408': [8.382209, 45.818935, 9.159376, 46.63229],
    '3534': [8.397363, 46.527614, 8.957809, 46.993407],
    '3564': [6.770579, 45.858557, 8.478518, 46.654047],
    '3588': [6.063663, 46.187813, 7.249184, 46.986909],
    '3821': [8.394969, 47.08103, 8.700868, 47.248375],
    '3823': [8.357845, 47.159443, 8.98495, 47.6944],
    '875': [36.19715, 33.45861, 36.355926, 33.571453],
    '119': [36.55303, 35.380944, 38.72015, 36.924123],
    '1301': [36.116831, 33.542549, 40.232659, 35.398867],
    '1762': [35.743448, 35.220992, 36.2587, 35.940066],
    '3356': [35.880366, 34.627729, 36.328143, 35.271057],
    '3321': [121.28281, 24.673165, 122.005938, 25.299327],
    '3323': [121.457015, 24.960341, 121.665112, 25.210236],
    '3326': [116.71475, 20.697487, 121.048795, 23.471304],
    '3277': [67.33692, 38.904543, 70.971944, 41.045094],
    '924': [68.657661, 38.457504, 68.964233, 38.64056],
    '1601': [36.97385, -4.62396, 38.470909, -2.84846],
    '228': [34.770883, -4.09931, 37.671786, -1.415869],
    '2014': [31.97183, -9.71725, 35.00074, -6.905773],
    '2127': [35.307571, -9.959255, 38.50328, -5.81372],
    '894': [35.094559, -7.414935, 36.99975, -4.21307],
    '3796': [39.185944, -6.321311, 39.305007, -6.076409],
    '2167': [31.86348, -3.437834, 33.790566, -1.462189],
    '3074': [31.68364, -4.326181, 34.234341, -3.198238],
    '680': [100.848976, 13.171568, 101.998871, 13.976628],
    '2187': [101.184982, 14.087219, 103.018883, 15.808054],
    '2552': [99.099511, 12.542498, 100.096488, 13.333054],
    '3284': [98.453323, 8.309998, 100.090132, 9.802109],
    '330': [100.327877, 13.49342, 100.938445, 13.955187],
    '1585': [101.749881, 15.620552, 103.194702, 17.076386],
    '2554': [99.885269, 16.347069, 101.095261, 17.748144],
    '703': [99.25972, 18.999959, 100.576705, 20.466063],
    '2185': [99.806931, 13.649137, 100.337517, 14.177298],
    '2280': [100.262433, 13.790533, 100.5676, 14.140231],
    '2617': [99.143418, 10.95361, 100.015514, 12.634093],
    '3159': [100.030548, 6.289443, 101.109131, 7.936218],
    '2562': [98.261192, 7.757711, 98.441162, 8.198694],
    '3454': [99.246461, 7.055554, 100.046356, 7.993332],
    '3109': [100.191933, 14.724781, 100.492477, 15.167498],
    '2723': [100.984154, 12.585261, 101.830551, 13.15361],
    '2558': [100.221039, 14.109442, 100.823126, 14.685831],
    '2548': [97.848902, 7.895464, 98.713318, 9.463623],
    '2510': [100.331582, 13.916274, 100.951683, 14.276049],
    '1654': [98.627083, 7.469805, 99.347504, 8.674999],
    '726': [100.667077, 12.595632, 101.717484, 13.590832],
    '702': [98.080826, 17.248329, 99.59166, 20.147538],
    '2922': [100.034714, 13.428196, 100.4203, 13.721092],
    '2921': [100.444847, 13.47915, 100.963896, 13.716971],
    '1512': [98.181697, 13.726142, 99.892212, 15.663012],
    '874': [125.503368, -8.615712, 125.852868, -8.134583],
    '1967': [0.712423, 6.11243, 1.798029, 6.922318],
    '2354': [30.740772, 1.365224, 35.000272, 4.218628],
    '957': [32.819528, -0.163887, 34.831583, 2.418939],
    '654': [31.012483, -1.046156, 34.080063, 1.677704],
    '3719': [29.573443, -1.482318, 32.362886, 2.5141],
    '583': [-56.493956, -34.879602, -55.393302, -34.207691],
    '2113': [-56.428397, -34.92946, -56.022501, -34.701853],
    '1918': [-55.483854, -34.972361, -54.464486, -33.928978],
    '1764': [-55.624184, -34.635499, -54.129059, -33.319428],
    '2930': [-57.15213, -34.783246, -56.342191, -33.833399],
    '3167': [-58.440585, -33.955603, -57.089246, -33.000755],
    '763': [-58.423537, -34.474907, -56.98232, -33.764634],
    '674': [-55.353237, -33.029397, -53.183776, -31.651707],
    '2517': [-58.221507, -32.595618, -56.238571, -31.452465],
    '2759': [-58.424526, -33.410703, -56.546395, -32.342697],
    '160': [71.495375, 40.401239, 73.148946, 41.059315],
    '1031': [70.328907, 39.886283, 72.223311, 40.772709],
    '2913': [65.12387, 39.29277, 67.504441, 40.619156],
    '537': [62.199035, 38.952417, 65.387873, 41.468047],
    '2663': [64.38673, 37.978051, 67.692537, 39.520271],
    '3360': [69.121886, 41.170553, 69.407607, 41.397936],
    '3069': [168.118262, -17.826274, 168.587921, -16.568633],
    '186': [-65.723476, 7.658483, -62.679831, 10.256822],
    '203': [-67.881794, 9.251794, -66.546142, 10.536489],
    '347': [-71.879348, 7.286395, -67.501571, 9.061536],
    '471': [-67.43996, 3.606122, -60.272897, 8.44338],
    '598': [-68.427561, 9.811266, -67.517349, 10.582984],
    '1019': [-71.315007, 10.301126, -68.221365, 12.195067],
    '1186': [-68.028919, 7.623364, -64.774386, 10.040532],
    '3582': [-67.412482, 10.388207, -66.309479, 10.632191],
    '1755': [-70.881041, 9.401902, -68.874858, 10.745339],
    '2035': [-71.915058, 7.66225, -70.543646, 9.197875],
    '2070': [-67.220507, 9.935113, -65.428223, 10.649857],
    '2097': [-64.062722, 8.382449, -61.996789, 10.319238],
    '2373': [-64.409583, 10.732056, -63.776733, 11.177502],
    '2611': [-70.202095, 8.091448, -68.512372, 9.841733],
    '3268': [-64.530706, 10.032721, -61.853286, 10.765104],
    '3312': [-72.489208, 7.36113, -71.316773, 8.648495],
    '3479': [-71.088531, 8.952097, -69.999851, 10.039165],
    '3755': [-69.208288, 9.834666, -68.248721, 10.774064],
    '3822': [-73.365857, 8.364143, -70.667631, 11.835996],
    '148': [104.780268, 10.203064, 105.56992, 10.95981],
    '276': [107.007234, 10.326837, 107.545917, 10.757099],
    '280': [105.968919, 21.165757, 107.101814, 21.617962],
    '283': [105.967416, 20.977621, 106.389959, 21.280538],
    '426': [108.655524, 13.537166, 109.301725, 14.716127],
    '427': [106.362919, 10.863275, 106.949469, 11.472676],
    '429': [107.398307, 10.543958, 108.839782, 11.552357],
    '578': [105.215102, 9.905816, 105.896284, 10.342838],
    '814': [107.819396, 15.932912, 108.291989, 16.208914],
    '1060': [107.484724, 12.170011, 109.016246, 13.43175],
    '871': [102.145883, 20.896006, 103.595923, 22.546985],
    '904': [106.728649, 10.544635, 107.552416, 11.546424],
    '1108': [107.451599, 12.996272, 108.878149, 14.614909],
    '1215': [105.81433, 20.365792, 106.231938, 20.697274],
    '1243': [105.362094, 20.546104, 106.073431, 21.398931],
    '1216': [105.126709, 17.918367, 106.509393, 18.677507],
    '1225': [106.198969, 20.692333, 106.732623, 21.272603],
    '1228': [106.422737, 20.630848, 106.912942, 21.079325],
    '1292': [106.408971, 10.3695, 107.014289, 11.127234],
    '1322': [105.958829, 20.608742, 106.298566, 21.018125],
    '1576': [108.681392, 11.806762, 109.46273, 12.846477],
    '1595': [103.839565, 9.425557, 105.548772, 10.539617],
    '1744': [107.261106, 11.25779, 108.728947, 12.321131],
    '2193': [105.932091, 19.96789, 106.57872, 20.478095],
    '2248': [103.884575, 18.564477, 105.808494, 20.035765],
    '2677': [107.219615, 14.983169, 108.743641, 16.058393],
    '2678': [108.315565, 14.564879, 109.086433, 15.408711],
    '2679': [106.568115, 20.841165, 108.031143, 21.653929],
    '3368': [105.809108, 10.928693, 106.487466, 11.772982],
    '3390': [106.167296, 20.314579, 106.632873, 20.722408],
    '3391': [105.514624, 21.349581, 106.278376, 22.04686],
    '3392': [104.390843, 19.290921, 106.074309, 20.655492],
    '3401': [107.081276, 15.996399, 108.195164, 16.74203],
    '3409': [105.804616, 10.236666, 106.792862, 10.572454],
    '3630': [105.366318, 21.140058, 105.859104, 21.585077],
    '947': [29.978338, -14.997935, 33.549311, -11.576827],
    '2332': [22.000368, -14.733354, 27.628904, -10.887828],
    '635': [25.218738, -16.564482, 31.436522, -12.002396],
    '3207': [24.967788, -18.077374, 28.856946, -15.470097],
    '1874': [27.769137, -16.31061, 30.420067, -14.642745],
    '776': [26.779438, -13.934904, 29.071047, -12.221695],
    '2338': [29.080049, -11.506149, 32.355996, -8.271976],
    '538': [28.387415, -20.258672, 28.696783, -19.961475],
    '1245': [30.877435, -18.050645, 31.221659, -17.665559],
    '1941': [31.188503, -21.327248, 33.068236, -17.245825],
    '1987': [30.046417, -17.740229, 32.755836, -15.620987],
    '1988': [30.554921, -19.260546, 32.993613, -16.695187],
    '1989': [28.015637, -18.911521, 30.962546, -15.609319],
    '1991': [29.730701, -22.424037, 32.497132, -19.148562],
    '1992': [25.237924, -20.36194, 29.233795, -16.878287],
    '2054': [27.997499, -21.043417, 30.870544, -17.11707],
    '135': [44.120615, 15.232007, 44.399674, 15.611707],
    '27': [43.388668, 12.631999, 45.09878, 12.941218],
    '20': [44.368773, 13.550131, 45.15636, 14.234621],
    '2421': [43.510652, 15.527984, 44.349468, 16.691201],
    '1582': [31.513695, 45.757186, 35.283321, 47.600194],
    '3648': [23.605366, 50.287269, 26.107289, 51.969136],
    '2771': [25.101568, 50.004052, 27.72988, 51.949713],
    '3810': [27.189697, 49.584181, 29.735213, 51.681434],
    '1596': [29.268838, 49.178099, 32.161026, 51.553778],
    '699': [30.485715, 50.345565, 33.500444, 52.375614],
    '3282': [32.94372, 50.110845, 35.68033, 52.366295],
    '1578': [34.856123, 48.531178, 38.093615, 50.459439],
    '1868': [37.83885, 47.825145, 40.22048, 50.087091],
    '903': [36.541952, 46.862888, 39.090623, 49.233878],
    '3797': [34.139465, 46.260808, 37.247088, 48.144698],
    '1877': [22.640894, 48.71827, 25.427004, 50.645907],
    '1412': [23.547612, 47.72461, 25.652545, 49.544086],
    '3457': [22.130458, 47.89767, 24.625479, 49.097272],
    '3383': [24.719593, 48.507484, 26.443342, 50.263585],
    '700': [24.908844, 47.725017, 27.532816, 48.6752],
    '2399': [28.187964, 45.194814, 31.304978, 48.233934],
    '2169': [30.207375, 46.402916, 33.176899, 48.232207],
    '796': [32.479584, 44.382889, 36.649529, 46.230151],
    '3632': [27.373045, 48.066267, 30.020113, 49.888928],
    '1583': [26.132767, 48.451209, 27.89796, 50.593491],
    '698': [29.613765, 48.452037, 32.896184, 50.228581],
    '2595': [32.09038, 48.726688, 35.491078, 50.553709],
    '886': [32.961447, 47.456615, 36.936887, 49.19071],
    '1612': [29.749466, 47.750014, 33.894311, 49.244714],
    '1696': [30.236391, 50.21335, 30.827578, 50.590583],
    '3051': [33.382084, 44.382057, 33.924438, 44.838749],
    '2335': [50.37276, 26.057142, 50.540085, 26.265107],
    '3212': [50.454743, 25.576652, 50.822107, 26.186892],
    '1928': [50.515458, 26.124048, 50.631087, 26.258421],
    '2151': [50.576366, 26.17965, 50.720076, 26.33114],
    '73': [47.541149, 28.524446, 48.430273, 29.206994],
    '75': [47.756157, 29.303053, 48.403091, 29.471926],
    '1267': [47.978796, 29.264487, 48.098922, 29.360332],
    '103': [51.08745, 24.474201, 51.629188, 25.225452],
    '307': [50.795853, 24.471111, 51.542362, 25.388119],
    '107': [50.750402, 25.080927, 51.32629, 25.808048],
    '306': [51.44851, 25.195287, 51.630652, 25.391505],
    '11': [51.533766, 22.631514, 56.017398, 25.251033],
    '69': [55.423222, 24.752625, 56.105553, 25.450703],
    '916': [54.890476, 24.624529, 56.205707, 25.356322],
    '1053': [55.964439, 24.866118, 56.376903, 25.660833],
    '1365': [55.731133, 24.820274, 56.27166, 26.069386],
    '3066': [55.349899, 24.75341, 56.381431, 25.625885],
    '1366': [55.521107, 25.275555, 55.928055, 25.645631],
    '24': [34.769683, 36.544215, 36.409578, 38.404602],
    '28': [37.435966, 37.409695, 39.263267, 38.210063],
    '34': [29.65889, 37.777488, 31.731986, 39.267023],
    '137': [34.978845, 40.233008, 36.512246, 41.077598],
    '183': [29.262114, 36.098554, 32.6236, 37.426941],
    '268': [27.00375, 37.337891, 28.904474, 38.128691],
    '312': [26.608668, 39.071974, 28.998508, 40.666248],
    '172': [30.836104, 38.661884, 33.885227, 40.753889],
    '71': [33.171239, 37.979907, 34.500613, 39.050927],
    '366': [40.986731, 37.493644, 41.821916, 38.550948],
    '422': [29.720592, 39.659867, 30.662445, 40.551366],
    '546': [28.106936, 39.593514, 29.908364, 40.61202],
    '579': [25.666616, 39.453282, 27.548696, 40.762],
    '788': [33.961316, 39.939436, 35.530935, 41.350946],
    '856': [28.569047, 36.881517, 30.092978, 38.474086],
    '883': [39.10654, 37.50644, 41.366581, 38.714584],
    '925': [30.822344, 40.622926, 31.853646, 41.115762],
    '962': [26.036679, 40.589931, 27.026315, 42.00221],
    '975': [38.356877, 38.239985, 40.334828, 39.191403],
    '994': [40.246042, 39.152102, 42.620187, 40.990786],
    '999': [29.988367, 39.040263, 32.010083, 40.187343],
    '1088': [36.442824, 36.705811, 38.081075, 37.527081],
    '1232': [43.306487, 36.959854, 44.815117, 37.810583],
    '1252': [35.782055, 35.812176, 36.706041, 37.008211],
    '1404': [30.092553, 37.342498, 31.539715, 38.509059],
    '1406': [27.972179, 40.803562, 29.958588, 41.590591],
    '1422': [26.230589, 37.81523, 28.493001, 39.385487],
    '1490': [36.201489, 37.180989, 37.751893, 38.586114],
    '1525': [31.973824, 40.825424, 33.137322, 41.587859],
    '1554': [34.849619, 37.691441, 36.95977, 39.322326],
    '1602': [36.712362, 36.623652, 37.572743, 37.034642],
    '1608': [26.884053, 41.217111, 28.14539, 42.104801],
    '1621': [29.335439, 40.526007, 30.365754, 41.209431],
    '1635': [31.258556, 36.751419, 34.436517, 39.304882],
    '1683': [28.608588, 38.79139, 30.446188, 39.878857],
    '1917': [37.260813, 37.866578, 39.151425, 39.15279],
    '1945': [27.112713, 38.085301, 29.135626, 39.407116],
    '1960': [39.88476, 36.911434, 41.897053, 37.736375],
    '2038': [32.5109, 36.026249, 35.151537, 37.435601],
    '2147': [27.225752, 36.293251, 29.771756, 37.600689],
    '2231': [34.238805, 38.281197, 35.166401, 39.383646],
    '2439': [36.68241, 40.32577, 38.116043, 41.147589],
    '2458': [35.876455, 36.929727, 36.696537, 37.74978],
    '2893': [29.966981, 40.286089, 31.012479, 41.177487],
    '2919': [34.878665, 40.82195, 37.169872, 41.73522],
    '3123': [35.854382, 38.537085, 38.77224, 40.552608],
    '3373': [26.693293, 40.538723, 28.144313, 41.609355],
    '3453': [39.123325, 40.513538, 40.507452, 41.103798],
    '3535': [28.754364, 38.165858, 30.034079, 38.935413],
    '3578': [42.662201, 37.72009, 44.512531, 39.370968],
    '3745': [28.779679, 40.457022, 29.558304, 40.736893],
    '3817': [31.292983, 41.00698, 32.294704, 41.588622],
    '2962': [37.83519, 36.669152, 40.238471, 38.020517],
    '3211': [34.267257, 29.493186, 35.475645, 31.882661],
    '641': [34.680593, 31.760954, 35.047042, 32.41083],
    '1449': [34.829316, 31.601989, 35.227483, 31.85141],
    '3374': [34.736771, 31.988156, 34.877413, 32.198905],
    '1226': [34.871221, 32.370857, 35.210631, 32.887958],
    '2344': [35.02974, 32.388667, 35.670294, 33.271705],
    '719': [90.589794, 20.972663, 92.631943, 24.243627],
    '863': [89.255267, 22.834361, 91.23626, 24.73543],
    '2710': [88.021675, 23.765046, 89.818217, 25.266411],
    '1553': [96.391605, 15.224547, 98.918682, 19.509061],
    '2217': [94.829729, 19.417322, 96.895983, 23.682046],
    '3753': [93.350413, 14.061429, 96.793724, 17.785992],
    '3062': [96.152077, 19.311754, 101.167578, 24.158785],
    '1902': [72.655418, 15.606171, 80.898357, 22.030225],
    '2177': [93.326638, 25.199105, 95.238305, 27.034139],
    '3543': [77.083948, 23.870134, 84.634682, 30.406334],
    '3337': [76.233405, 8.076252, 80.346423, 13.564496],
    '701': [80.244311, 17.782357, 84.39615, 24.106294],
    '1199': [68.172028, 20.119152, 74.476569, 24.712428],
    '847': [76.839035, 28.404692, 77.347396, 28.882107],
    '3100': [88.013479, 27.079449, 89.82804, 28.246987],
    '3473': [91.149713, 22.939663, 92.331546, 24.527411],
    '3685': [85.820024, 21.547457, 89.874198, 27.220733],
    '2400': [81.388362, 17.812921, 87.486062, 22.567512],
    '417': [83.320238, 24.286305, 88.299741, 27.521088],
    '2709': [69.456935, 23.06122, 78.272254, 30.198128],
    '3375': [77.235913, 15.836026, 81.322634, 19.916703],
    '159': [76.760595, 12.624244, 84.764742, 19.16729],
    '227': [91.562308, 26.65102, 97.395359, 29.382748],
    '2023': [89.819683, 25.029249, 92.802822, 26.119502],
    '1453': [83.329877, 21.972553, 87.962287, 25.349341],
    '235': [89.700177, 24.136238, 96.017781, 27.971569],
    '1944': [92.970635, 23.833184, 94.745216, 25.691798],
    '1537': [74.085688, 11.59567, 78.587615, 18.458838],
    '3545': [77.5723, 28.722928, 81.03086, 31.286852],
    '815': [70.876685, 20.048193, 73.217971, 20.742846],
    '1887': [74.029696, 21.070106, 82.810313, 26.869065],
    '1125': [73.675382, 14.900642, 74.336009, 15.800763],
    '1565': [74.868011, 8.292489, 77.41232, 12.795609],
    '690': [76.705152, 30.666547, 76.849381, 30.795267],
    '158': [92.21125, 6.758093, 93.940346, 13.665718],
    '1288': [75.594341, 30.377701, 78.997207, 33.256636],
    '1436': [73.750034, 32.277012, 76.779524, 34.787141],
    '2650': [73.879848, 29.542805, 76.939418, 32.512697],
    '1250': [74.473639, 27.652599, 77.604297, 30.92722],
    '483': [1.974457, 8.757715, 3.836434, 10.690651],
    '123': [2.032983, 10.452103, 3.835723, 12.396943],
    '1811': [2.346022, 6.340913, 2.528383, 6.411858],
    '2469': [2.406149, 6.358826, 2.721439, 6.973295],
    '248': [1.966637, 6.304306, 2.475845, 6.971319],
    '2254': [1.923689, 13.431011, 2.257589, 13.633011],
    '801': [7.840874, 4.689173, 9.474327, 6.896413],
    '1012': [6.779962, 8.419336, 7.72408, 9.408685],
    '2407': [2.705177, 6.297285, 4.609586, 7.951043],
    '2477': [2.692613, 7.080647, 4.568998, 9.188311],
    '3151': [4.140757, 11.530315, 6.792772, 13.885645],
    '3789': [4.860745, 10.963452, 7.246568, 13.182451],
    '1737': [2.705038, 6.368742, 4.36863, 6.698423],
    '72': [7.460275, 4.473337, 8.327949, 5.516196],
    '381': [5.376114, 4.270585, 6.607241, 5.379249],
    '2424': [4.38277, 5.888719, 6.004126, 7.750246],
    '848': [4.977438, 5.018384, 6.769402, 6.501234],
    '2769': [6.398394, 4.32528, 7.588843, 5.689925],
    '1692': [2.742958, 8.008471, 6.208536, 10.156666],
    '1625': [5.321503, 6.557928, 7.802608, 8.738791],
    '403': [7.530514, 6.442982, 9.888608, 8.158168],
    '486': [11.544609, 10.024699, 14.677038, 13.721435],
    '1546': [6.8544, 11.128836, 9.047338, 13.342273],
    '2577': [8.555038, 8.367342, 10.637917, 10.380446],
    '963': [5.015619, 5.748114, 6.715769, 7.591417],
    '1456': [8.144535, 10.94221, 10.605592, 13.014821],
    '154': [6.600627, 5.681609, 7.339063, 6.753429],
    '1519': [7.675171, 10.543359, 9.360312, 12.602578],
    '2213': [6.934631, 7.770002, 9.61753, 9.329255],
    '1555': [3.496789, 10.10058, 6.204432, 13.263686],
    '1370': [6.63908, 5.194552, 7.432286, 5.938931],
    '23': [11.341521, 7.391217, 13.729559, 10.948336],
    '3768': [9.674965, 10.570278, 12.497416, 13.380594],
    '6': [7.141856, 4.82827, 7.986323, 6.04215],
    '965': [4.863862, 7.283187, 5.791615, 8.111246],
    '2460': [4.068734, 6.980735, 5.057633, 8.095503],
    '371': [8.745787, 9.447322, 11.008532, 12.532466],
    '2262': [3.581944, 8.201951, 7.378645, 11.387495],
    '1484': [6.092243, 9.001431, 8.818318, 11.51917],
    '989': [6.860966, 5.92464, 7.903396, 7.10155],
    '3346': [9.115717, 6.500008, 11.876282, 9.615353],
    '960': [7.505153, 5.6848, 8.456825, 6.792406],
    '1087': [34.218699, 31.220052, 34.5678, 31.594539],
    '3683': [34.880274, 31.342602, 35.574052, 32.552088],
    '824': [-17.104628, 20.769984, -8.667525, 27.66727],
  },
  [LocationType.COUNTY]: {},
  [LocationType.CITY]: {},
};
